import kit from '@binaa-almajal/ui-kit/locales/kit/en';

// Flat key => string. Interpolation is {name} — see I18nProvider.t.
//
// The copied dictionary was 1706 lines of a personal-finance app (zakat, nisab,
// treasuries, exchange rates). What survives here is the generic UI vocabulary
// the component kit needs, plus the Academy's own terms. Add keys as screens
// land, and keep ar.js in step — a missing key falls back to English silently,
// which reads as a rendering bug rather than a translation gap.

// The kit's own copy comes first and this file's keys override it: a string
// the kit ships (`table.columns`, `shortcut.close`) arrives here translated,
// vendored with the component that reads it, and an application that wants a
// different word for one of them simply writes that key below.
export default {
    ...kit,
    // ─── Application ─────────────────────────────────────────────────────────
    'app.name': 'VDC Academy',
    'app.logoAlt': 'Binaa Al-Majal logo',

    // ─── Brand ───────────────────────────────────────────────────────────────
    // The company behind the Academy, as the logo artwork states it. Kept
    // separate from `app.name` (the product is sold as the VDC Academy, the same
    // name as `landing.brandName`, signed in or not): `app.name` is what the product is called in a header and a title bar,
    // `brand.name` is the company being credited on the splash and in the
    // footer's copyright line. Writing "Binaa Al-Majal Academy © 2026" in a
    // footer would credit a school rather than its publisher.
    //
    // Translated rather than left in English because the splash is the one
    // screen shown before anyone has chosen a language — it renders in whatever
    // the browser asked for, and half-translating it would be worse than either
    // extreme.
    'brand.name': 'Binaa Al-Majal',
    'brand.tagline': 'Building smarter. Modeling better. Delivering future.',
    'brand.discipline': 'Digital construction',

    // ─── Navigation ──────────────────────────────────────────────────────────
    'nav.dashboard': 'Dashboard',
    'nav.people': 'People',
    'nav.cohorts': 'Cohorts',
    'nav.arrivals': 'Arrivals',
    'nav.cohortRequests': 'Cohort requests',
    'nav.grading': 'Grading',
    'nav.enrollmentsQueue': 'Awaiting completion',
    'nav.credentials': 'Credentials',
    'nav.curriculum': 'Curriculum',
    'nav.my': 'My learning',
    'nav.my.what': 'What to do next, and what is due',
    // The five groups. Each names the JOB, not the entity behind it — "Queues"
    // rather than "Registrations, Grading, Trainer applications, Readiness",
    // which is what the bar used to say out loud.
    'nav.group.home': 'Home',
    'nav.group.queues': 'Queues',
    'nav.group.register': 'Register',
    'nav.group.catalogue': 'Catalogue',

    // ─── What each sidebar entry holds ───────────────────────────────────────
    // The second line under every entry in the sidebar. They started on the
    // catalogue doors, because they all ended at a list of modules with
    // thresholds and the labels alone rank the doors onto what looks like the
    // same room. They cover the
    // whole bar now: four described rows among a dozen bare ones read as four
    // rows singled out for a reason the copy never gave.
    //
    // Kept to four words or so, and every one names what the screen HOLDS
    // rather than what a person does there — the verb belongs to the buttons
    // inside. This is a disambiguator, not documentation.
    'nav.dashboard.what': 'Today at a glance',
    'nav.company.what': 'Your seats and people',
    'nav.arrivals.what': 'Newly added learners',
    'nav.cohortRequests.what': 'Companies asking for dates',
    'nav.grading.what': 'Work awaiting a verdict',
    'nav.enrollmentsQueue.what': 'People who finished and are not closed out',
    'nav.people.what': 'Everyone on record',
    'nav.cohorts.what': 'Programs actually running',
    'nav.availability': 'Availability',
    'nav.availability.what': 'Who is away, from HR',
    'nav.credentials.what': 'Who holds what',
    'nav.companies.what': 'Who buys the seats',
    'nav.curriculum.what': 'What we teach',
    'nav.programs.what': 'What we sell',
    'nav.checkCodes.what': 'What a grader may cite',

    // ─── The catalogue pipeline strip ────────────────────────────────────────
    // One caption per step in CataloguePipeline. Each says what the step HOLDS,
    // because the strip's position already says where it sits.
    'pipeline.curriculum': 'Levels, modules, lessons',
    'pipeline.programs': 'Modules bundled to sell',
    'pipeline.cohorts': 'A program, actually running',
    'nav.group.system': 'System',
    // The sidebar's own controls: the rail toggle at its foot, and the menu
    // button Header shows on phones.
    'nav.menu': 'Open navigation',

    // ─── Identity ────────────────────────────────────────────────────────────
    'auth.signIn': 'Sign in',
    'auth.signOut': 'Sign out',
    'auth.signInRequired': 'Sign in to continue.',

    // ── The front door (screens/SignIn.jsx) ──
    'auth.gateLead': 'Training, assessment and credentialing for the Binaa platform.',
    'auth.gateTitle': 'Sign in to continue',
    // Names the redirect, so the identity provider's own page is expected
    // rather than alarming. There is no password field on our side and there
    // is not going to be one.
    'auth.gateSignInHint': 'You will be taken to the Academy sign-in page to enter your credentials.',
    'auth.gateNoProvider': 'No sign-in provider is configured for this build.',
    'auth.noAccountTitle': 'No account yet?',
    // Accounts are issued by the Academy after a deal — the way in is the
    // enquiry form, not self-registration (which no longer exists).
    'auth.noAccountEnquiryHint': 'Accounts are issued by the Academy. Get in touch and our team will set everything up for you.',
    'auth.getInTouch': 'Get in touch',
    'auth.haveAccount': 'Already have an account?',
    'auth.gateVerify': 'Verify Certificate',
    // Shown for actor_unmatched. Deliberately does NOT suggest signing in again:
    // the token is valid and re-authenticating can never fix it.
    'auth.notProvisioned':
        'Your account is not linked to a person record yet. Ask an administrator to link it — signing in again will not help.',
    'auth.notProvisionedTitle': 'Your account needs to be linked',
    'auth.notProvisionedQuote': 'Give the administrator this identity:',
    'auth.claimHint':
        'Registered and been approved? If you signed in with the email address you registered under, you can claim your records yourself.',
    'auth.claimAction': 'Claim my records',
    'auth.claimNotFound':
        'No approved registration matches this email address. If you registered under a different address, ask an administrator to link your account instead.',
    'error.generic': 'Something went wrong.',

    // ─── Common ──────────────────────────────────────────────────────────────
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.backHome': 'Back to the dashboard',
    'common.toggleTheme': 'Toggle theme',
    'common.or': 'or',

    // ─── Markets ─────────────────────────────────────────────────────────────
    'market.select': 'Select a country…',
    'market.none': 'No countries available',
    // The empty multi-select. Empty means "sold everywhere" — the migrated
    // meaning of the nullable column — and saying so stops it reading as unset.
    'market.allMarkets': 'No countries selected — available everywhere',
    'market.label': 'Countries',
    'market.add': 'Add a country…',
    'market.remove': 'Remove {code}',
    'market.all': 'All countries',
    'countryFilter.label': 'Filter by country',
    'countryFilter.all': 'All countries',

    // Preferred language. Chosen from the languages the app ships dictionaries
    // for — a value outside that set silently falls back to English.
    'person.languageNotSet': 'Not set',
    'person.languageUnsupported': 'not a supported language',
    'common.user': 'User',
    'common.userMenu': 'User menu',
    'common.yes': 'Yes',
    'common.no': 'No',

    'action.close': 'Close',

    // ─── Dashboard ─────────────────────────────────────────────────────────────
    'dashboard.purpose': 'What needs your attention, and where to go about it.',
    'screen.dashboard.intro': 'What needs your attention, and where to go about it — start with Needs you, then the queues below it.',
    'screen.cohorts.intro': 'Programs actually running — open one to see its roster, dates and instructor.',
    'dashboard.people': 'People',
    'dashboard.learners': 'Learners',
    'dashboard.instructors': 'Instructors',
    // The scoped row (SnapshotHeader.jsx): an instructor reading their OWN
    // narrowed snapshot, where `people`/`learners` are already the same
    // number and `instructors` is 0-or-1 — themselves. Worded as theirs, not
    // as the academy's.
    'dashboard.myLearners': 'My learners',
    'dashboard.myLearnersHint': 'Across the cohorts you teach',
    'dashboard.myTeachingStatus': 'Teaching status',
    'dashboard.teachingNow': 'Teaching now',
    'dashboard.notTeaching': 'Not currently teaching',
    'dashboard.gradingQueue': 'Awaiting grading',
    'dashboard.outboxDepth': 'Outbox depth',
    'dashboard.outboxHint': 'Integration events waiting to drain',
    'dashboard.cohortsByState': 'Cohorts by state',
    'dashboard.enrollmentsByState': 'Enrollments by state',
    'dashboard.credentialsByStatus': 'Credentials by status',
    'dashboard.noCohortsYet': 'No cohorts yet',
    'dashboard.noEnrollmentsYet': 'No enrollments yet',
    'dashboard.noCredentialsYet': 'No credentials yet',
    // The donut's own centre label — just "total", the same word for any of
    // the three breakdowns it sits inside.
    'dashboard.total': 'Total',
    'dashboard.openCohorts': 'Open the cohort register',
    'dashboard.programScoringTitle': '{count} programmes have no module scoring',
    'dashboard.programScoringTitleOne': '1 programme has no module scoring',
    'dashboard.programScoringBody': 'Their programme bar sets no score thresholds, so credentials only require passes. Set a minimum score on the modules that should demand one.',
    'dashboard.programScoringCta': 'Set module scoring',
    // The four queues. One shared empty line: zero means the same calm thing in
    // all four, and four ways of saying "nothing waiting" is four things to
    // translate and keep in step for no gain.
    'dashboard.queues': 'Waiting on a decision',
    'dashboard.queueEmpty': 'Nothing waiting',
    'dashboard.gradingQueueHint': 'Assessments waiting on an instructor',
    // The second queue (PLATFORM-PLAN-3 §1c): finished every module, still not
    // marked complete. The wording says what the person did, not what the row
    // looks like — "enrolled at 100%" is a chart artefact, not a sentence.
    'dashboard.awaitingCompletionQueue': 'Awaiting close-out',
    'dashboard.awaitingCompletionQueueHint':
        'Finished every module and not yet marked complete',
    // ─── Delivery (PLATFORM-PLAN-3 §1c.1) ────────────────────────────────────
    // Every label here is about TEACHING, which is the half this dashboard never
    // had. Nothing in this block is money: the Academy owns the minutes, the
    // HRM owns their cost.
    'dashboard.delivery': 'Delivery',
    'dashboard.hoursTaught': 'Hours taught',
    'dashboard.hoursTaughtHint': 'Across {held} sessions held',
    'dashboard.sessionsHeld': 'Sessions held',
    'dashboard.lastHeld': 'Last held {when}',
    'dashboard.neverHeld': 'None held yet',
    'dashboard.attendanceRate': 'Attendance',
    // NOT "0%". Nothing has been asserted, which is a different fact from
    // nobody coming.
    'dashboard.noRegisters': 'No register taken yet',
    'dashboard.attendanceBasis': '{present} of {expected}, across {registers} registers',
    'dashboard.cancellationRate': 'Cancellations',
    'dashboard.nothingDecided': 'Nothing held or cancelled yet',
    'dashboard.cancellationBasis': '{cancelled} cancelled · {scheduled} still to run',
    'dashboard.instructorsTeaching': 'Teaching now',
    'dashboard.instructorsTeachingHint': 'Named on a cohort that is live',
    'dashboard.instructorsDelisted': 'Delisted',
    'dashboard.instructorsDelistedHint': 'No longer taking cohorts',
    'dashboard.satisfaction': 'Satisfaction',
    'dashboard.unrated': 'Not rated yet',
    // The scores are an NPS-scale 0..10; rendering them as a percentage invents
    // a claim the ratings never made.
    'dashboard.outOfTen': '{score} / 10',
    'dashboard.ratingsCount': '{count} ratings',
    'dashboard.sessionsAhead': 'Scheduled',
    'dashboard.sessionsAheadHint': 'Classes still to run',
    'dashboard.sessionsByOutcome': 'Sessions by outcome',
    'dashboard.instructorDelivery': 'Instructors, by delivery',
    'dashboard.instructorDeliveryLead':
        'Who taught, how much, and how it went. Delisted instructors stay on the list and sort last — a delivery record does not disappear when somebody stops teaching.',
    'dashboard.noInstructors': 'No instructor has taught here yet',
    'dashboard.noInstructorsHint':
        'Instructors appear once a cohort in this market names them.',
    'dashboard.delisted': 'Delisted',
    'dashboard.heldOfScheduled': '{held} held · {ahead} ahead',
    'dashboard.cancelled': 'Cancelled',
    'dashboard.cancelledCount': '{count} ({rate})',
    'dashboard.cohortsTaught': 'Cohorts',
    'dashboard.cohortsRunningOf': '{total}, {running} running',
    'dashboard.learnersTaught': 'People taught',
    'dashboard.lastTaught': 'Last taught',

    // ─── Public verification ─────────────────────────────────────────────────
    'verify.title': 'Verify a certificate',
    'verify.subtitle': 'Enter the number printed on the certificate. No account is needed.',
    'verify.placeholder': 'Certificate number',
    'verify.submit': 'Verify',
    'verify.notFound': 'No certificate was found with that number.',
    'verify.rateLimited': 'Too many checks from this network. Wait a minute and try again.',
    'verify.publicNo': 'Certificate number',
    'verify.issuer': 'Issued by',
    'verify.issuedOn': 'Issued on',
    'verify.validUntil': 'Valid until',
    'verify.competencies': 'Competencies',

    'credential.status.Active': 'Active',
    'credential.status.Expired': 'Expired',
    'credential.status.Revoked': 'Revoked',
    'credential.status.Superseded': 'Superseded',

    // ─── Errors ──────────────────────────────────────────────────────────────
    // Keys are the server's machine codes, which arrive in the error body's `code`.
    // errorMessage() looks up `error.<code>`, then falls back to the server's
    // `detail` — which names the actual cohort or module — then to error.unknown.
    'error.not_found': 'That record no longer exists.',
    'error.conflict': 'That change conflicts with the current state.',
    'error.validation': 'The request was rejected.',
    'error.forbidden': 'You do not have permission to do that.',
    'error.seat_capacity': 'That cohort is full.',
    'error.curriculum_frozen': 'This curriculum version is pinned by a cohort — its structure, credits and pass marks can no longer change. Lessons, material and briefs stay editable.',
    'error.instructor_delisted': 'You are the assigned instructor, but your standing is delisted. Re-certification restores authoring.',
    'error.cohort_closed': 'This cohort is completed or cancelled — its delivery content is read-only.',
    'error.quiz_unconfigured': 'This quiz is not ready to be taken — tell your coordinator.',
    'error.requirements_unmet': 'The programme bar has not been met.',
    'error.program_unconfigured': 'This programme has no mandatory modules, so there is no bar to issue against.',
    'error.already_enrolled': 'This person is already enrolled in that cohort.',
    'error.not_confirmable': 'That assessment is not in a confirmable state.',
    'error.ai_unavailable': 'AI drafting is unavailable.',
    'error.ai_quota_reached': 'The AI usage limit for this period is reached. Please try again later, or continue by hand.',
    'error.artifact_unavailable': 'File storage is not set up on this deployment.',
    'error.live_unavailable': 'Live classes are not set up on this deployment.',
    'error.credential_lapsed': 'That credential has lapsed.',
    'error.stale_write': 'Someone else saved first. Reload, reapply your change, and save again.',
    'error.idempotency_mismatch': 'This request cannot be retried. Reload the page and start again.',
    'error.actor_unmatched':
        'Your account is not linked to a person record. Ask an administrator to link it.',

    'error.unauthenticated': 'Your session is not valid. Sign in again.',
    'error.rate_limited': 'Too many requests. Please wait a moment.',
    'error.model_binding': 'Some fields were rejected before the request reached the server.',
    'error.network': 'The server could not be reached.',

    'error.pageNotFound': 'We could not find that page.',

    // ─── Connection ──────────────────────────────────────────────────────────
    'connection.serverUnreachable': 'Cannot reach the Academy',

    // ─── Validation ──────────────────────────────────────────────────────────
    'validation.fieldRequired': '{field} is required',
    'validation.emailInvalid': 'Enter a valid email address',
    'validation.nameMinLength': '{field} must be at least 2 characters',
    'validation.nameLettersOnly': '{field} can only contain letters, spaces, hyphens and apostrophes',
    'validation.nameLatin': 'Enter the name in English (Latin) letters — certificates are issued in English and print it exactly as written here.',
    'validation.minLength': 'Must be at least {min} characters',
    'validation.maxLength': 'Must be no more than {max} characters',
    'validation.invalidNumber': '{field} must be a valid number',
    'validation.fieldPositive': '{field} must be greater than 0',
    'validation.fieldDate': '{field} must be a valid date',
    'validation.invalidPhone': 'Enter a valid phone number',
    'phone.countryLabel': 'Country and dialling code',
    'phone.numberPlaceholder': 'Phone number',
    'phone.noCountry': 'No country',
    'validation.invalidUrl': 'Enter a valid URL',
    'validation.fileType': 'File type must be one of: {types}',
    'validation.fileSize': 'File must be smaller than {size}MB',

    // ─── Component kit ───────────────────────────────────────────────────────
    'search.placeholder': 'Search…',
    'search.group.screens': 'Screens',
    'search.group.tabs': 'Tabs',
    'search.group.people': 'People',
    'search.group.cohorts': 'Cohorts',




    'date.pick': 'Pick a date',
    'date.today': 'Today',
    'date.tomorrow': 'Tomorrow',
    'date.yesterday': 'Yesterday',

    'uploadForm.chooseFile': 'Choose a file',
    'uploadForm.dropFile': 'Drop a file here',
    'uploadForm.uploadFile': 'Upload',
    'uploadForm.downloadTemplate': 'Download template',
    'uploadForm.templateText': 'Use the template so the columns match.',

    // ─── Domain kit — lifecycle states (both wire casings normalise here) ────
    'state.draft': 'Draft',
    'state.filling': 'Filling',
    'state.confirmed': 'Confirmed',
    'state.running': 'Running',
    'state.completed': 'Completed',
    'state.cancelled': 'Cancelled',
    'state.invited': 'Invited',
    'state.active': 'Active',
    'state.withdrawn': 'Withdrawn',
    'state.failed': 'Failed',
    'state.awaitingInstructor': 'Awaiting instructor',
    'state.returned': 'Returned',
    'state.expired': 'Expired',
    'state.revoked': 'Revoked',
    'state.superseded': 'Superseded',
    'state.submitted': 'Submitted',
    'state.approved': 'Approved',
    'state.rejected': 'Rejected',
    'state.awaitingApproval': 'Awaiting approval',
    'state.pending': 'Pending',
    'state.passed': 'Passed',
    'state.notReady': 'Not ready',
    'state.conditionallyReady': 'Conditionally ready',
    'state.ready': 'Ready',
    'state.notStarted': 'Not started',
    'state.inProgress': 'In progress',
    'state.notAttempted': 'Not attempted',
    'state.waived': 'Waived',

    // ─── Domain kit — batch outcomes ─────────────────────────────────────────
    'batch.allSucceeded': 'All {count} items succeeded',
    'batch.partialFailure': '{failed} of {total} items failed',

    // ─── Domain kit — stale write ────────────────────────────────────────────
    'staleWrite.title': 'Someone else saved first',
    'staleWrite.hint': 'Reload to get their version — your unsaved changes stay in the form so you can review and save again.',
    'staleWrite.reload': 'Reload record',


    // ─── Domain kit — competency ─────────────────────────────────────────────
    'competency.trainingProgress': 'Training progress',
    'competency.trainingProgressHint': 'Content consumed — not a competence claim',
    'competency.passedCreditRatio': 'Passed credit ratio',
    'competency.passedCreditRatioHint': 'Passed credits over assessable credits',
    'competency.mandatoryOutstanding': 'Mandatory modules are still outstanding.',
    'competency.noModulesOnTrack': 'No modules on this track',
    'competency.progressShort': 'Progress',
    'competency.passedShort': 'Passed',
    'competency.modulesShort': 'Modules',
    'competency.noModules': 'No modules yet',
    'competency.noModulesHint': 'The cohort’s program has no modules attached. Module rows appear once the program is configured.',
    'competency.module': 'Module',
    'competency.completion': 'Completion',
    'competency.assessment': 'Assessment',
    'competency.bestScore': 'Best score',
    'competency.credits': 'Credits',
    'competency.notAssessedModule': 'Not assessed',

    // ─── Domain kit — programme bar ──────────────────────────────────────────────
    'programBar.title': 'Programme bar',
    'programBar.meets': 'Meets programme bar',
    'programBar.notYet': 'Not yet',
    'programBar.unconfigured': 'No bar set',
    'programBar.unconfiguredHint': 'This programme has no mandatory modules, so there is no bar to meet yet. An administrator sets it on the programme.',
    'programBar.unmetHeading': 'Still to meet',
    'programBar.unmetCount': '{count} module(s) outstanding',
    'programBar.reasonNotPassed': 'not passed',
    'programBar.reasonScore': 'score {actual} below {required}',
    'programBar.reasonNoScore': 'no score yet (needs {required})',
    'programBar.reasonCapstone': 'capstone missing',

    // ─── Cursor pager ────────────────────────────────────────────────────────

    // ─── Cohort register columns ─────────────────────────────────────────────
    'cohort.code': 'Code',
    'cohort.program': 'Program',
    'cohort.market': 'Country',
    'cohort.state': 'State',
    'cohort.fill': 'Seats',
    'cohort.startsOn': 'Starts',
    'cohort.instructor': 'Instructor',
    'cohort.searchPlaceholder': 'Search cohorts by code or programme…',
    'cohort.stateAll': 'Every state',
    'cohort.unstaffedOnly': 'No instructor yet',
    'cohort.noneMatch': 'No cohort matches',
    'cohort.noneMatchHint': 'Clear the search or the filters to see the whole register.',
    'cohort.statTotal': 'Cohorts',
    'cohort.statRunning': 'Running',
    'cohort.statFilling': 'Filling',
    'cohort.statUnstaffed': 'Unstaffed',

    // ─── Trainee 360 ─────────────────────────────────────────────────────────
    'trainee.title': 'Trainee 360',
    'trainee.enrolledAt': 'Enrolled {date}',
    'trainee.dimensions': 'Dimensions',
    'trainee.modules': 'Modules',

    // ─── Enrollment detail ───────────────────────────────────────────────────
    'enrollment.title': 'Enrollment',
    'enrollment.completedAt': 'Completed {date}',
    'enrollment.dualCredit': 'Dual credit',

    // ─── People ──────────────────────────────────────────────────────────────
    'person.fullName': 'Name',
    'person.fullNameLatinHint': 'In English letters, exactly as it should appear on certificates.',
    'person.email': 'Email',
    'person.market': 'Country',
    'person.preferredLanguage': 'Preferred language',
    // ── The register's tabs (WS18c) ─────────────────────────────────────────
    // FILTERS OVER FACTS, not a discriminator: a graduate who came back to
    // teach appears under both Learners and Instructors, and "Unaffiliated" is
    // an ordinary state — a staff administrator is neither.
    'person.kindLearner': 'Learners',
    'person.kindManager': 'Company managers',
    'person.kindInstructor': 'Instructors',
    'person.kindUnaffiliated': 'Unaffiliated',
    'person.statTotal': 'People',
    'person.statLearners': 'Learners',
    'person.statInstructors': 'Instructors',
    'person.statUnaffiliated': 'Unaffiliated',
    'person.company': 'Company',
    // ONLY ON THE INSTRUCTOR TAB. A learner and a company manager cannot be
    // added by hand at all — they exist because something was bought — and an
    // instructor exists only because a coordinator says so.
    'person.profiles': 'Profiles',
    'person.learner': 'Learner',
    'person.instructor': 'Instructor',
    'person.identity': 'Sign-in identity',
    'person.identityLinked': 'Linked',
    'person.identityNotLinked': 'Not linked — nobody can sign in as this person',
    // ─── Arrivals ─────────────────────────────────────────────────────────────
    'arrivals.subtitleHrm': 'What the employer assigned — internal employees sent to train or named to teach. Issue their sign-in, then place learners into a cohort.',
    'arrivals.subtitle': 'Newly arrived customers — verify the person or company, issue their sign-in, then enroll from the confirmed offer.',
    'arrivals.total': 'Arrivals',
    'arrivals.totalHint': 'Every door, under the current filter',
    'arrivals.awaitingAdmissionCount': 'Awaiting admission',
    'arrivals.awaitingAdmissionHint': 'Payment cleared, admission needs a click',
    'arrivals.notPlacedCount': 'Not placed yet',
    'arrivals.notPlacedHint': 'Can sign in, sits in no cohort',
    'arrivals.loginIssuedCount': 'Access issued',
    'arrivals.loginIssuedHint': 'Of {total} shown',
    'arrivals.received': 'Received',
    'arrivals.who': 'Who',
    'arrivals.kind': 'Account',
    'arrivals.person': 'Person',
    'arrivals.company': 'Company',
    'arrivals.instructor': 'Instructor',
    'arrivals.learner': 'Learner',
    'arrivals.role': 'Arrived as',
    'arrivals.sourceAll': 'All doors',
    'arrivals.sourceCrm': 'From sales',
    'arrivals.sourceHrm': 'From the employer',
    'arrivals.sourceAcademy': 'Bought directly',
    'arrivals.all': 'All',
    'arrivals.people': 'People',
    'arrivals.companies': 'Companies',
    // The company tab narrowed: a university is a company account with a kind.
    'arrivals.universities': 'Universities',
    'arrivals.university': 'University',
    'arrivals.seats': '{count} seats',
    'arrivals.deal': 'Deal',
    'arrivals.access': 'Access',
    'arrivals.loginIssued': 'Login issued',
    'arrivals.placed': 'Placed in',
    'arrivals.seatsPlaced': '{placed} of {seats} seats placed',
    'arrivals.notPlaced': 'Not placed yet',
    'arrivals.notPlacedYet': 'Not placed in a cohort yet — place them before they sign in, or they will find nothing to attend.',
    'arrivals.invited': 'Invited',
    'arrivals.offer': 'Offer',
    // ── THE SECOND HANDOVER, and the wording carries the whole distinction ──
    // "Issue login" gives the coordinator a password to read down the phone.
    // This one gives them nothing, because nobody here ever holds it — the
    // customer sets their own from a link Keycloak sends. The acknowledgement
    // says what was QUEUED, not what the customer can now do: when it appears,
    // Nexus has not been asked yet.
    'arrivals.searchPlaceholder': 'Search by name, email or deal…',
    'arrivals.empty': 'No arrivals yet',
    'arrivals.emptyHint': 'Converted enquiries and won deals land here as they happen.',
    'arrivals.emptyHintHrm': 'Employees the HRM assigns to training — or names as instructors — land here as it happens.',
    'arrivals.offerTitle': 'Offer — {name}',
    'arrivals.requirements': 'Requirements',
    'arrivals.programs': 'Programmes',
    'arrivals.modules': 'Modules',
    'arrivals.moduleVerified': 'Verified',
    'arrivals.moduleUnverified': 'Not verified',
    'arrivals.attachments': 'Attachments',
    'arrivals.offerEmpty': 'The offer carries no structured details.',

    'person.emptyList': 'No people yet',
    'person.searchPlaceholder': 'Search by name or email…',
    'person.newArrival': 'New',
    'person.newArrivalsBanner': '{count} new arrivals are waiting for their sign-in — review them.',
    'person.signInAccess': 'Sign-in access',
    'person.signInActive': 'Can sign in',
    'person.signInNone': 'No sign-in yet',
    'person.signInActiveHint': 'This person has an account and signs in with their email address.',
    'person.signInNoneHint': 'Issue a sign-in to create their account: they get their email address and a temporary password, changed at first sign-in.',
    'person.signInNoneAutoLink': 'When this person signs in, they will be linked automatically by their email address.',
    'person.issueSignIn': 'Issue sign-in',
    'person.resetSignIn': 'Reset password',
    'person.signInIssuedLead': 'The account for {email} is ready. Its temporary password:',
    'person.copy': 'Copy',
    'person.copied': 'Copied',
    'person.signInEmailQueued': 'The same details were emailed to them.',
    'person.signInIssuedOnce': 'The password is shown only this once and must be changed at first sign-in.',
    'person.identityAdvanced': 'Advanced: link an existing identity',
    'person.emptyListHint': 'People are created by approving a registration or directly by the enrollment desk.',
    'person.learnerProfile': 'Learner profile',
    'person.instructorProfile': 'Instructor profile',
    'person.upgradedAt': 'Upgraded',
    'person.crmAccount': 'External account',
    'person.tier': 'Tier',
    'person.nps': 'NPS',
    'person.certifiedAt': 'Certified',
    'person.delistedAt': 'Delisted',
    'person.instructorInactive': 'Inactive',
    'person.noProfiles': 'No learner or instructor profile — an ordinary shape for a staff administrator.',
    'person.tabProfile': 'Profile',
    'person.tabCredentials': 'Credentials',

    'learnerTrack.internalHire': 'Internal hire',
    'learnerTrack.external': 'External',
    'instructorKind.founder': 'Founder',
    'instructorKind.dualHat': 'Dual hat',
    'instructorKind.certifiedTrainer': 'Certified trainer',
    'trainerTier.associate': 'Associate',
    'trainerTier.professional': 'Professional',
    'trainerTier.master': 'Master',

    // ─── Credentials (holder view) ───────────────────────────────────────────
    'credential.emptyForHolder': 'No credentials',
    'credential.emptyForHolderHint': 'Credentials issued to this person appear here, including revoked and expired ones.',
    'credential.issuedOn': 'Issued',
    'credential.validUntil': 'Valid until',

    // ─── Readiness (person tab) ──────────────────────────────────────────────

    // ─── Audit ───────────────────────────────────────────────────────────────
    'availability.title': 'Instructor availability',
    'availability.subtitle': 'Approved absences, stated by the HR system. Nothing here blocks a booking — a session over an absence is a warning to read, not a refused write.',
    'availability.from': 'From',
    'availability.to': 'To',
    'availability.person': 'Person',
    'availability.startDate': 'First day away',
    'availability.endDate': 'Last day away',
    'availability.statedAt': 'Stated',
    'availability.statTotal': 'Absences in window',
    'availability.statCurrentlyAway': 'Away today',
    'availability.statInstructors': 'Instructors affected',
    'availability.statUnlinked': 'Not linked to anyone here',
    'availability.notLinked': 'Not linked to anyone here',
    'availability.lastApplied': 'Last delivery {when} ({ago}), {count} absences stated.',
    'availability.empty': 'Nobody is away in this window',
    'availability.emptyHint': 'The feed has delivered — this is a quiet window, not a missing one.',
    'availability.neverDelivered': 'No leave has ever been delivered',
    'availability.neverDeliveredHint': 'The HR system has never stated an absence to this Academy, so an empty list here means nothing.',

    // ─── People writes ───────────────────────────────────────────────────────
    'person.emailOptional': 'Optional — a person can exist without one.',
    'person.marketHint': 'The person\u2019s country.',
    'person.clearableHint': 'Leave empty and save to clear this field — clearing writes NULL, deliberately.',
    'person.clearField': 'Clear',
    'person.noLearnerProfile': 'No learner profile. Attaching one is what makes this person enrollable.',
    'person.noInstructorProfile': 'No instructor profile. Instructors are assigned from the HRM, not from this screen.',
    'person.learnerTrack': 'Track',
    'person.crmAccountHint': 'Optional crm.account id for a corporate learner.',
    'person.identityLink': 'Sign-in identity link',
    'person.identityLinkedTo': 'Linked to identity user id',
    'person.identityUserId': 'Nexus person id',
    'person.identityLinkHint': 'Records who a token subject IS. It grants nothing — roles live in Keycloak. This is also the fix for a caller stuck on "account not linked".',
    'person.linkIdentity': 'Link',
    'person.rateInstructor': 'Rate instructor',
    'person.ratingCohort': 'Cohort ID',
    'person.ratingScore': 'Score (0–10)',
    'person.ratingScoreHint': 'Append-only: a correction is a new rating, never an edit.',
    'person.ratingComment': 'Comment',
    'person.ratingSource': 'Source',
    'person.submitRating': 'Submit rating',
    'person.recordAudit': 'Record audit',
    'person.auditReviewedAt': 'Reviewed on',
    'person.auditOutcome': 'Outcome',
    'person.auditDelistWarning': 'Delisting also stamps the instructor profile, in the same transaction.',
    'person.auditFindings': 'Findings',
    'person.submitAudit': 'Record outcome',

    'inputSource.instructor': 'Instructor',
    'inputSource.lms': 'LMS',
    'inputSource.import': 'Import',
    'inputSource.selfReported': 'Self-reported',
    'auditOutcome.passed': 'Passed',
    'auditOutcome.coached': 'Coached',
    'auditOutcome.downTiered': 'Down-tiered',
    'auditOutcome.delisted': 'Delisted',

    // ─── Cohorts ─────────────────────────────────────────────────────────────
    'cohort.create': 'New cohort',
    'cohort.emptyListHint': 'Create the first cohort to start filling seats.',
    'cohort.availability': 'Availability',
    'cohort.seatsAvailable': '{count} seats open',
    'cohort.notAccepting': 'Not accepting',
    'cohort.curriculum': 'Curriculum',
    'cohort.selectCurriculumFirst': 'Select a curriculum first.',
    'cohort.curriculumVersion': 'Curriculum version',
    'cohort.currentVersion': '{label} (current)',
    'cohort.pinFreezesVersion': 'Creating this cohort pins the selected curriculum version and freezes its STRUCTURE — levels, modules, credits and pass marks are refused from then on. Lessons, material and assignment briefs stay editable.',
    'cohort.format': 'Format',
    'cohort.sourcing': 'Sourcing',
    'cohort.deliveryLayer': 'Delivery layer',
    'cohort.seats': 'Seats',
    'cohort.statFilled': 'Filled',
    'cohort.statFilledOf': '{filled} of {seats}',
    'cohort.statActive': 'Active',
    'cohort.statFinished': 'Finished',
    'cohort.statSeatsOpen': 'Seats open',
    'cohort.seating': 'Taught at',
    'cohort.seatingHint': 'Whose room and whose machines. The asset register is told about lab seats only for cohorts taught on our own premises.',
    'cohortSeating.unstated': 'Not stated',
    'cohortSeating.remote': 'Remote',
    'cohortSeating.academyPremises': 'Our premises',
    'cohortSeating.clientPremises': 'Client premises',
    'cohort.endsOn': 'Ends',
    'cohort.instructorOptional': 'Optional — a Draft cohort without an instructor is normal.',
    'cohort.versionNotEditable': 'Pinned to {label}. The curriculum version is not editable by design — re-pinning would regrade learners.',
    'cohort.moveTo': 'Move to:',
    'cohort.viewList': 'List',
    'cohort.viewBoard': 'Board',
    'cohort.boardDropHere': 'Drop cohorts here',
    'cohort.moveToAria': 'Move {code} to another state',
    'cohort.everyoneFinished': 'All {count} learners have finished. Ready to close this cohort?',
    'cohort.everyoneFinishedShort': 'Every learner has finished',
    'cohort.completeNow': 'Complete cohort',
    'cohort.confirmTransition': 'Confirm transition',
    'cohort.transitionCopy': 'Move {code} to',
    'cohort.cancelWarning': 'Cancelling is terminal. The cohort cannot be reopened.',
    'cohort.confirmMove': 'Confirm',
    'cohort.assignInstructor': 'Assign instructor',
    'cohort.currentInstructor': 'Currently: {name}',
    'cohort.assign': 'Assign',
    'cohort.linkCrmAccount': 'Link company',
    'cohort.crmAccountId': 'Company account ID',
    // Pick from the accounts the Academy already holds, or type an id below
    // for one it does not. Picking a university defaults the role.
    'cohort.crmAccountPick': 'Company or university',
    'cohort.selectCompany': 'Select a company or university...',
    'cohort.noCompaniesYet': 'No companies yet — create one first',
    'cohort.crmAccountPickHint': 'Choosing one fills the id below and suggests the role; you can still change either.',
    'cohort.crmRole': 'Role',
    'cohort.link': 'Link',
    'cohort.crmAccount': 'Company',
    'cohort.tabOverview': 'Overview',
    'cohort.tabEnrollments': 'Enrollments',
    'cohort.tabContent': 'Content',
    'cohort.tabEngagement': 'Engagement',

    'cohortFormat.corporateBootcamp': 'Corporate bootcamp',
    'cohortFormat.openEnrollment': 'Open enrollment',
    'cohortFormat.universityDiploma': 'University diploma',
    'cohortFormat.selfPacedLms': 'Self-paced LMS',
    'cohortFormat.certificationPrep': 'Certification prep',
    'cohortFormat.executiveMasterclass': 'Executive masterclass',
    'cohortFormat.recruitmentLinked': 'Recruitment-linked',
    'cohortSourcing.academySourced': 'Academy-sourced',
    'cohortSourcing.selfSourced': 'Self-sourced',
    'crmRole.corporateClient': 'Corporate client',
    'crmRole.universityPartner': 'University partner',

    // ─── Enrolling ───────────────────────────────────────────────────────────
    'enroll.title': 'Enroll into {code}',
    'enroll.learner': 'Learner',
    'enroll.submit': 'Enroll',
    'enroll.offerAnotherCohort': 'Find another cohort with open seats',
    'enroll.goToExisting': 'Open this cohort’s register',
    'enroll.learnersArriveFromTheCrm': 'Learners arrive from a converted deal, or from a company adding an employee to its own roster. There is no way to make one from this screen.',
    'enroll.batch': 'Batch enroll',
    'enroll.batchTitle': 'Batch enroll',
    'enroll.addLearner': 'Add learner',
    'enroll.batchSubmit': 'Enroll {count} learners',
    'enroll.emptyRegister': 'Nobody is enrolled yet',
    'enroll.emptyRegisterHint': 'Enroll a learner, or approve a registration that names this cohort.',
    'enroll.enrolledAt': 'Enrolled',
    'enroll.detail': 'Competency',
    'enroll.openDetail': 'Open',
    'enrollmentsQueue.lead': 'Every module finished, the enrollment still active — open a learner to close them out, or the cohort to close the whole class.',
    'enrollmentsQueue.empty': 'Nobody is waiting to be closed out',
    'enrollmentsQueue.emptyHint': 'A learner appears here the moment their last module completes.',
    'enrollmentsQueue.totalWaiting': 'Awaiting completion',
    'enrollmentsQueue.totalWaitingHint': 'Every module finished, not yet closed out',
    'enrollmentsQueue.dualCreditWaiting': 'Dual credit waiting',
    'enrollmentsQueue.dualCreditWaitingHint': 'A university diploma rides on these',
    'enrollmentsQueue.oldestWaiting': 'Longest waiting',
    'enrollmentsQueue.oldestWaitingHint': 'Enrolled {when}',
    'enrollmentsQueue.noneWaiting': 'None waiting',
    'enroll.moveTo': 'Move to:',
    'enroll.confirmTransition': 'Confirm transition',
    'enroll.transitionCopy': 'Move {name} to',
    'enroll.occurredAt': 'Occurred on',
    'enroll.occurredAtHint': 'When it HAPPENED — not when it was typed.',
    'enroll.reevaluate': 'Reevaluate',
    'enroll.reevaluateHint': 'Force a fresh competency evaluation after out-of-band changes.',

    // ─── Batch vocabulary ────────────────────────────────────────────────────
    'batch.mode': 'Batch mode',
    'batch.chooseMode': 'Choose how failures behave…',
    'batch.modeHint': 'Best effort by default: enrol whoever can be enrolled and name who could not. Atomic rolls the whole batch back on one refusal.',
    'batch.atomic': 'Atomic — all or nothing',
    'batch.atomicHint': 'One refusal rolls the whole batch back. Nothing was written.',
    'batch.bestEffort': 'Best effort — per-item outcomes',
    'batch.bestEffortHint': 'Each item succeeds or fails on its own; review the per-item outcomes below.',

    // ─── Completion recording ────────────────────────────────────────────────
    'completion.title': 'Record completion',
    'completion.module': 'Module',
    'completion.pct': 'Completion %',
    'completion.pctHint': '0–100 here; there is no score field — a pass comes from confirming an assessment.',
    'completion.state': 'State',
    'completion.source': 'Source',
    'completion.note': 'Note',
    'completion.submit': 'Record',
    'completionBatch.title': 'Bulk completion import',
    'completionBatch.hint': 'Paste CSV rows in LMS-export shape. The header row is optional; rows are validated before anything is sent.',
    'completionBatch.invalidLine': 'Line',
    'completionBatch.skippedRows': '{count} rows will be skipped:',
    'completionBatch.parsed': '{count} rows parsed and ready.',
    'completionBatch.submit': 'Import {count} rows',

    // ─── Waive ───────────────────────────────────────────────────────────────
    'waive.title': 'Waive module',
    'waive.signerNote': 'A waiver substitutes for a pass by AUTHORITY, not by mark. You are the signer — the record carries your identity from the token.',
    'waive.reason': 'Reason',
    'waive.reasonHint': 'Required. Recognised prior learning, transfer credit — the reason is the record.',
    'waive.nothingToWaive': 'Every module is already passed or waived.',
    'waive.submit': 'Waive',

    // ─── Registration kind (the public lead form) ───────────────────
    // The rest of this block went with the admission queue (PLATFORM-PLAN
    // WS7). What is left is the one question /register still asks: whether
    // the person is writing as a would-be learner or a would-be instructor.
    'registration.kind.learner': 'Learner',
    'registration.kind.instructor': 'Instructor',

    // ─── Public intake ───────────────────────────────────────────────────────
    'register.title': 'Apply to the Academy',
    'register.subtitle': 'Tell us about yourself and our team will get in touch. Applying creates no account — nothing is set up for you until we have spoken.',
    'register.emailHint': 'We reply to this address, and sign-in is matched against it later — use one you control.',
    'register.kind': 'I am applying as',
    'register.message': 'Anything we should know? (optional)',
    'register.messageHint': 'What you want to learn or teach, your background, timing.',
    'register.submit': 'Send application',
    'register.receivedTitle': 'Application received',
    'register.receivedCopy': 'Our team will read it and reply to the email you gave. There is no status page to check, and nothing has been created in your name yet.',

    // ─── Pickers ─────────────────────────────────────────────────────────────
    'picker.person': 'Search people…',
    'picker.noPeople': 'No matching people',

    // ─── Grading ─────────────────────────────────────────────────────────────
    'grading.allKinds': 'All',
    'grading.emptyQueue': 'Nothing awaiting grading',
    'grading.emptyQueueHint': 'The normal steady state for a well-run cohort — new submissions appear here.',
    'grading.openDetail': 'Open',
    // ── Instructor quiz marking (ACADEMY-AI-WORKFLOWS-PLAN §6.3/§6.6) ────────
    'grading.quizzesTab': 'Quizzes',
    'grading.quizzesEmpty': 'No quizzes waiting',
    'grading.quizzesEmptyHint': 'Free-text answers appear here once a learner submits them.',
    'grading.aiCouldNotMark': "AI couldn't mark",
    'grading.markedByInstructorBadge': 'Instructor',
    'grading.quizMarkingTitle': 'Mark quiz answers',
    'grading.markMode': 'Marking',
    'grading.overrideMode': 'Override',
    'grading.overrideBanner': 'Changing a recorded mark is audited.',
    'grading.choiceScoreLine': 'Choice questions: {earned} of {possible} points',
    'grading.markingGuide': 'Marking guide',
    'grading.emptyAnswer': '(no answer given)',
    'grading.aiSuggestion': 'AI suggests {points} points',
    'grading.useSuggestion': 'Use suggestion',
    'grading.pointsLabel': 'Points (max {max})',
    'grading.feedbackLabel': 'Feedback',
    'grading.saveMarks': 'Save marks',
    'grading.aiDraftingInfo': 'AI is drafting a mark — you can also grade now.',
    'grading.scoreShort': 'Score',
    'grading.detailTitle': 'Assessment',
    'grading.submission': 'Submission',
    'grading.resubmit': 'Resubmit',
    'grading.resubmitNote': 'Saving a new submission clears the return block and puts this back in the instructor’s queue.',
    'grading.checks': 'Rubric checks',
    'grading.noChecks': 'No check findings. With an empty vocabulary, instructors grade unaided — that is a working system.',
    'grading.finalScore': 'Final score',
    'grading.confirm': 'Confirm grade',
    'grading.confirmerNote': 'You are the confirmer — the record carries your identity from the token.',
    'grading.notConfirmable': 'This assessment is no longer confirmable — someone else may have acted first.',
    'grading.refreshQueue': 'Refresh the queue',
    'grading.confirmedScore': 'Confirmed: {score}',
    'grading.returnTitle': 'Return to learner',
    'grading.returnReason': 'Reason',
    'grading.returnReasonHint': 'Required — the reason IS the returned state’s content; without it the learner sees “Returned” and nothing else.',
    'grading.returnConfirm': 'Return',
    'grading.returnedTitle': 'Returned to the learner',
    'grading.returnedBy': 'By',
    'grading.aiDraft': 'AI draft',
    'grading.aiDraftHint': 'Writes a draft score and check findings. Never a grade.',
    'grading.aiDraftLabel': 'AI draft — never a grade',
    'grading.aiDraftScore': 'AI draft: {score}',
    'grading.batchSelected': '{count} assessments selected with scores',
    'grading.batchConfirm': 'Confirm {count}',
    'grading.openTitle': 'Open assessment',
    'grading.kind': 'Kind',
    'grading.enrollmentModuleId': 'Enrollment module ID',
    'grading.enrollmentId': 'Enrollment ID',
    'grading.capstoneSpecId': 'Capstone spec ID',
    'grading.capstoneSpecHint': 'Required — which level’s capstone this is. An unattributed capstone cannot certify.',
    'grading.artifact': 'Artifact',
    'grading.artifactHint': 'Uploaded browser-direct to storage after the assessment opens; the API only signs and records.',
    'grading.openSubmit': 'Open',
    'grading.openArtifact': 'Open artifact',
    'grading.handedInAt': 'Handed in by the learner on {when}',
    'grading.handedInUnrecorded': 'No hand-in was recorded — this was opened by staff.',
    'grading.artifactUploadFailed': 'The artifact upload failed — the assessment was opened without it.',
    'grading.openedButArtifactFailed': 'The assessment was opened, but:',

    'assessmentKind.moduleRubric': 'Module rubric',
    'assessmentKind.capstone': 'Capstone',
    'verdict.pass': 'Pass',
    'verdict.gap': 'Gap',
    'verdict.fail': 'Fail',
    'verdict.notApplicable': 'N/A',

    // ─── Credentials (issue / lifecycle) ─────────────────────────────────────
    // The credentials home. There is no list endpoint — the API answers about a
    // holder — so the copy asks for a person rather than promising a register.
    'credential.homeSubtitle': 'Issue, look up and verify what the Academy has awarded.',
    'credential.byHolder': 'Look up a holder',
    'credential.pickHolder': 'Choose a person',
    'credential.pickHolderHint': 'Credentials are held by people: pick the holder to see their full history, including revoked and expired rows.',
    'credential.publicVerifier': 'Open the public verifier',
    'credential.detailTitle': 'Credential',
    'credential.publicVerify': 'Public verification',
    'credential.holder': 'Holder',
    'credential.holderSnapshot': 'Holder (at issuance)',
    'credential.holderSnapshotNote': 'The holder name is a snapshot taken at issuance — the certificate says what it said when it was printed, even if the person’s name has changed since.',
    'credential.program': 'Programme',
    'credential.renews': 'Renews',
    'credential.previousCredential': 'Previous credential',
    'credential.noCompetencies': 'No competencies recorded on this credential.',
    'credential.issueTitle': 'Issue credential',
    'credential.issueModuleTitle': 'Module certificate',
    'credential.issueConfirm': 'Issue',
    'credential.permanentNote': 'Issuance is a permanent public act: the credential becomes anonymously verifiable the moment it exists.',
    'credential.programUnconfigured': 'This programme has no bar set',
    'credential.programUnconfiguredCta': 'Open the programme to set its mandatory modules',
    'credential.renewTitle': 'Renew',
    'credential.renewNote': 'Renewal creates a NEW credential; this one remains as its history.',
    'credential.renewConfirm': 'Renew',
    'credential.revokeTitle': 'Revoke',
    'credential.revokeWarning': 'Revoking {publicNo} is a permanent public statement — the anonymous verifier will report it, not hide it.',
    'credential.revokeReason': 'Reason',
    'credential.revokeConfirm': 'Revoke',

    // ─── Programme modules editor ───────────────────────────────────────────
    'track.moduleId': 'Module ID',
    'track.module': 'Module',
    'track.saveWholeSet': 'Save all {count} requirements',

    // ─── Framework ──────────────────────────────────────────────────
    'framework.effectiveFrom': 'Effective from',
    'framework.roleTitle': 'Title',

    // ─── Readiness ───────────────────────────────────────────────────────────
    // Its own, rather than borrowed from the admission queue's reject modal —
    // that screen is deleted (PLATFORM-PLAN WS7), and a readiness rejection was
    // never a registration one.

    // ─── Curriculum ─────────────────────────────────────────────────
    'curriculum.versionLabel': 'Version',
    'curriculum.current': 'Current',
    'curriculum.frozen': 'Frozen',
    'curriculum.frozenBadge': 'Frozen',
    'curriculum.levels': 'Levels',
    'curriculum.modules': 'Modules',
    'curriculum.retiredOn': 'Retired',
    'curriculum.retired': 'Retired {date}',
    'curriculum.createVersion': 'New version',
    'curriculum.emptyList': 'No curriculum versions',
    'curriculum.emptyListHint': 'Create the version that cohorts will pin.',
    'curriculum.frozenExplanation': 'A cohort has pinned this version, freezing its structure: levels, modules, credits and pass marks are refused (curriculum_frozen) because they are retroactive. Lessons, material and briefs stay editable — content appears in no denominator. Structural changes belong in a new version.',
    'curriculum.createNewVersionCta': 'Create a new version instead',
    'curriculum.publish': 'Publish',
    'curriculum.retire': 'Retire',
    'curriculum.createLevel': 'New level',
    'curriculum.levelCreated': 'Level created.',
    'curriculum.levelIdHint': 'The new level appears in the structure below — add modules from there.',
    'curriculum.levelId': 'Level ID',
    'curriculum.levelIdPasteHint': 'From the create-level receipt.',
    'curriculum.level': 'Level',
    'curriculum.createModule': 'New module',
    'curriculum.codePermanent': 'Permanent from creation — requirements reference it and it can never be renamed.',
    'curriculum.ordinal': 'Ordinal',
    'curriculum.overview': 'Overview',
    'curriculum.flagship': 'Flagship',
    'curriculum.treeTitle': 'Structure',
    'curriculum.noLevels': 'No levels yet',
    'curriculum.noLevelsHint': 'A version publishes only once it has levels — create the first one.',
    'curriculum.levelOrdinal': 'Level {ordinal}',
    'curriculum.levelNoModules': 'No modules in this level yet.',
    'curriculum.creditsShort': '{credits} cr',
    'curriculum.capstone': 'Capstone',
    'curriculum.dimensionsTitle': 'Dimensions',
    'curriculum.differentiator': 'Differentiator',
    'curriculum.dimensionNoModules': 'No modules behind this dimension — a real gap, not a rendering problem.',
    'curriculum.dimensionModuleCount': '{count} module(s)',
    'curriculum.dimensionNotTaught': 'Not taught in {curriculum}.',
    'curriculum.dimensionsNoneTaught': 'No module in {curriculum} is tagged with a dimension yet.',
    'curriculum.dimensionsShowUntaught': 'Show the {count} dimension(s) {curriculum} does not teach',
    'curriculum.dimensionsHideUntaught': 'Hide the {count} dimension(s) {curriculum} does not teach',
    'curriculum.codeNotEditable': 'Code {code} is not editable by design — programme contents and every unmet-requirement message reference it.',
    'curriculum.retroactiveHint': 'Retroactive — refused once the version is frozen.',
    'curriculum.isAssessed': 'Assessed',
    'curriculum.passScore': 'Pass score',
    'curriculum.assessmentMarking': 'Assessment first mark',
    'curriculum.assessmentMarkingAiHint': 'AI drafts a score and checks; an instructor always confirms.',
    'curriculum.assessmentMarkingNoCodesHint': 'AI drafts need check codes. Author them in',
    'curriculum.checkCodesLinkLabel': 'Check codes',
    'curriculum.assessmentMarkingNoCodesAskAdmin': 'AI drafts need check codes — ask an administrator to author them.',
    'curriculum.assessed': 'Assessed · pass ≥ {score}',
    'curriculum.notAssessed': 'Not assessed — feeds progress only, never proficiency',
    'curriculum.dimensions': 'Dimensions',
    'curriculum.tools': 'Skills & Tools',
    'curriculum.noCodes': 'No codes yet.',

    // ─── CURRICULA: the programmes of study themselves ───────────────────────
    // A curriculum is WHICH SUBJECT (VDC, Project Management); a version is
    // WHICH EDITION of it. Conflating them is what made a second live version
    // the only way to teach a second subject.
    'curriculum.curriculum': 'Curriculum',
    'curriculum.curricula': 'Curricula',
    'curriculum.curriculaHint': 'The programmes of study the academy teaches.',
    'curriculum.allCurricula': 'All curricula',
    'curriculum.createCurriculum': 'New curriculum',
    'curriculum.curriculumCode': 'Code',
    'curriculum.curriculumCodeHint': 'Short, uppercase, and permanent from creation — reports and requirements name it.',
    'curriculum.curriculumName': 'Name',
    'curriculum.curriculumSummary': 'Summary',
    'curriculum.curriculumSummaryHint': 'One or two sentences for the public page.',
    'curriculum.createCurriculumNext': 'A curriculum teaches nothing until it has a version with levels and modules. Create one next — and if this subject starts from an existing one, copy its structure across.',
    'curriculum.versionCount': '{count} version(s)',
    'curriculum.noCurricula': 'No curricula',
    'curriculum.noCurriculaHint': 'Create the programme of study that versions and cohorts hang off.',
    'curriculum.deleteCurriculum': 'Delete curriculum',
    'curriculum.deleteCurriculumConfirm': 'Delete {name}? Only a curriculum with no versions can be removed — nothing has been taught under it.',
    'curriculum.editCurriculum': 'Edit curriculum',
    'curriculum.curriculumCodeLocked': 'Set once and never changed — reports and requirements name it. A curriculum with the wrong code is deleted while it holds no versions, and made again.',
    'curriculum.curriculumOrdinal': 'Display order',
    'curriculum.curriculumOrdinalHint': 'Lower reads first, here and on the public page. Someone has to say which subject leads; created-at answers it by accident of typing speed.',
    'curriculum.versions': 'Versions',
    'curriculum.statCurricula': 'Curricula',
    'curriculum.statTotalVersions': 'Versions',
    'curriculum.statLive': 'Live now',
    'curriculum.statMultiLive': 'Double-booked',
    'curriculum.versionsOfAll': 'Every edition of every curriculum. Pick a curriculum above to narrow this to one subject.',
    'curriculum.versionsOfScoped': 'The editions of {curriculum}. Cohorts pin one of these and are graded against exactly it.',
    'curriculum.editVersion': 'Edit version',
    'curriculum.editVersionHint': 'The label and the date this edition takes effect.',
    'curriculum.dimensionsUnscoped': 'Module counts across every curriculum and every edition — what the academy has ever put behind each axis. Pick a curriculum above to count only its own.',
    'curriculum.dimensionsScoped': 'Module counts for {curriculum}, across all of its editions. A zero here means this subject teaches nothing under that axis, not that nobody does.',
    'curriculum.pickCurriculum': 'Pick a curriculum',
    'curriculum.versionCurriculumHint': 'Which programme of study this is an edition of. Cohorts on other curricula are unaffected.',

    // ─── A SECOND CURRICULUM VERSION ─────────────────────────────────────────
    // See docs/MULTI-CURRICULUM.md. A new version is a decision, not a row:
    // cohorts stay where they are, requirements do not follow, and two live
    // versions is an accident with a public consequence.
    'curriculum.live': 'Live',
    'curriculum.rendered': 'On the public page',
    'curriculum.multiLiveTitle': '{curriculum}: {count} versions are live at once',
    'curriculum.multiLiveBody': 'All of them are effective today and none is retired, but the public page renders only one — {label}. The others are live and invisible. This is almost always an accident: retire the ones that are not the academy, or move their effective dates.',
    'curriculum.costsTitle': 'What creating a version does — and does not do',
    'curriculum.costCohorts': 'Existing cohorts stay on their current version and are graded against it. Nothing moves underneath them.',
    'curriculum.costRequirements': 'Programme contents — and with them each programme’s module scoring — do NOT follow. They name module IDs, and the new version has new ones — until they are carried across, a cohort on this version can pass everything and be issued no credential.',
    'curriculum.costPublic': 'The public page ignores the new version until its effective date arrives, and two versions may not share one.',
    'curriculum.copyFrom': 'Copy structure and programmes from',
    'curriculum.copyFromNone': 'Start empty (structure entered by hand)',
    'curriculum.copyFromHint': 'Copies levels, modules and their tags, and remaps programme contents (with their module scoring) onto the new modules by code.',
    'curriculum.copyPreview': 'Preview the copy',
    'curriculum.copyPreviewTitle': 'What would be carried across',
    'curriculum.copyAppliedTitle': 'Carried across',
    'curriculum.copyCounts': '{levels} level(s), {modules} module(s), {programModules} programme module(s).',
    'curriculum.copyUnresolved': 'Not carried across',
    'curriculum.copyClean': 'Everything resolved — nothing was left behind.',
    'curriculum.copyFailed': 'The version was created; copying its structure failed. Copy again from the version screen.',
    'curriculum.deleteVersion': 'Delete',
    'curriculum.deleteVersionConfirm': 'Delete version {label}? This removes its levels and modules outright. It is refused if any cohort or programme depends on it — for a version with real history, retire it instead.',
    'curriculum.deleteVersionDone': 'Version deleted.',

    // ─── Programs ────────────────────────────────────────────────────────────
    'nav.programs': 'Programs',
    'program.name': 'Name',
    'program.roleSku': 'Role SKU',
    'program.standards': 'Standards',
    'program.statTotal': 'Programs',
    'program.statFlagship': 'Flagship',
    'program.statUnbundled': 'No modules yet',
    'program.create': 'New program',
    'program.codePermanent': 'The code is the reference other records use — not patchable later.',
    'program.emptyList': 'No programs',
    'program.emptyListHint': 'A program is what a cohort teaches — modules in order, against standards.',
    'program.modulesNote': 'The arrangement saves WHOLESALE — ordinals and optional flags are one arrangement, not row edits.',
    'program.optional': 'Optional',
    'program.requiresCapstone': 'Requires capstone',
    'program.minScore': 'Minimum score',
    'program.minScorePlaceholder': 'e.g. 70',
    'program.addModule': 'Add module',
    'program.noModules': 'No modules attached — enrollments in cohorts teaching this program will have empty module lists until they are.',
    // The prose — PLATFORM-PLAN-5 §5b.2.
    'program.about': 'About the programme',
    // Who it is sold to — PLATFORM-PLAN-5 §5b.1. `noneChosen` says what empty
    // MEANS, because empty here is the opposite of empty in the market field
    // one control away, and a coordinator who reads them the same way round
    // believes an untagged programme is on all three audience pages.
    'program.audience': 'Sold to',
    'program.audienceHint': 'Which of the three public doors list this programme. It stays in the catalogue either way.',
    'program.audience.businesses': 'Businesses',
    'program.audience.individuals': 'Individuals',
    'program.audience.universities': 'Universities',
    'program.audience.noneChosen': 'Nobody has said yet — this programme is listed under none of the three doors, and stays in the catalogue.',
    'program.overview': 'Overview',
    'program.overviewHint': 'The paragraph a buyer reads first. Up to {max} characters — longer is refused, not cut.',
    'program.overviewMissing': 'No overview written yet.',
    'program.outcomes': 'Outcomes',
    'program.outcomesHint': 'What a learner will be able to do afterwards, in the order they should read. Up to {max}, each up to {each} characters.',
    'program.outcomesCount': '{count} of {max}',
    'program.outcomesEmpty': 'No outcomes written yet.',
    'program.outcomeAdd': 'Add an outcome',
    'program.outcomePlaceholder': 'What a learner will be able to do…',
    // The lowercase-noun form ReorderableList's own "Remove {item}" / "Move
    // {item} earlier" wrap around, and each row's own field label.
    'program.outcomeOrdinal': 'Outcome {n}',
    'program.durationWeeks': 'Duration (weeks)',
    'program.durationWeeksHint': 'The nominal length of a cohort teaching it. Leave blank when it is not stated.',
    'program.weeks': '{n} weeks',
    'program.notWritten': 'Not written yet. The public page offers this programme by its name and modules alone until an overview, outcomes or a duration are written here.',

    // ─── Check codes ─────────────────────────────────────────────────────────
    'nav.checkCodes': 'Check codes',
    'checkCode.create': 'New check code',
    'checkCode.edit': 'Edit {code}',
    'checkCode.empty': 'No check codes defined',
    'checkCode.emptyHint': 'The correct initial state: instructors grade unaided and AI drafting stays off. That is a working system.',
    'checkCode.showRetired': 'Show retired',
    'checkCode.retired': 'Retired',
    'checkCode.retire': 'Retire',
    'checkCode.reactivate': 'Reactivate',
    'checkCode.neverDelete': 'Codes are never deleted — a code on a past assessment must stay resolvable forever.',
    'checkCode.everyKind': 'Every kind',
    'checkCode.label': 'Label',
    'checkCode.description': 'Description',
    'checkCode.severity': 'Severity',
    'checkCode.versionLabel': 'Version label',
    'checkCode.versionLabelHint': 'Required — what a check meant when it was raised is not recoverable from the current row.',
    'checkCode.kinds': 'Applies to kinds',
    'checkCode.kindsHint': 'None checked = applies to every assessment kind.',
    'checkCode.permanentNote': 'Entries are PERMANENT: the code can never be renamed or deleted, only retired.',
    'checkCode.codeImmutable': 'The code itself cannot change — it is written onto assessment rows. Retire and add instead.',

    'severity.advisory': 'Advisory',
    'severity.minor': 'Minor',
    'severity.major': 'Major',
    'severity.critical': 'Critical',

    // ─── Trainer pathway ─────────────────────────────────────────────────────

    // ─── Health ──────────────────────────────────────────────────────────────

    // ─── My records (the learner portal) ─────────────────────────────────────
    'my.title': 'My records',
    'my.subtitle': 'Your enrollments and credentials, as the Academy holds them.',
    'my.profile': 'Profile',
    'my.enrollments': 'My enrollments',
    'my.noEnrollments': 'No enrollments yet',
    'my.noEnrollmentsHint': 'When you are enrolled in a cohort, it appears here.',
    'my.enrolledOn': 'Enrolled {date}',
    'my.continueLearning': 'Continue learning',
    'my.credentials': 'My credentials',
    'my.noCredentials': 'No credentials yet',
    'my.noCredentialsHint': 'Credentials you earn appear here, with a public verification link for each.',
    'my.verifyLink': 'Public verification page',
    // Staff only: a coordinator's sign-in legitimately has no learner record.
    // Never "access denied" — nothing is being withheld.
    'my.noRecord': 'You have no learner record',
    'my.noRecordHint': 'Your account is not linked to the Academy. Contact an administrator to link your identity.',
    // Staff who are linked but have no learner profile yet — they can become one.
    'my.staffNoLearner': 'No training record yet',
    'my.staffNoLearnerHint': 'Your staff account can also take training. Click the button below to start your learner journey.',
    'my.becomeLearner': 'Start as a learner',

    // ───────────────────────────────── module content authoring (phase 8)

    'contentKind.text': 'Text',
    'contentKind.link': 'Link',
    'contentKind.video': 'Video',
    'contentKind.document': 'Document',
    'contentKind.meeting': 'Meeting',

    'lessons.title': 'Lessons',
    'lessons.addLesson': 'Add lesson',
    'lessons.editLesson': 'Edit lesson',
    'lessons.summary': 'Summary',
    'lessons.emptyTitle': 'No lessons yet',
    'lessons.emptyHint': 'Add the first lesson — the canonical plan every cohort teaches from. Lessons stay editable even on a frozen version.',
    'lessons.noContent': 'No material on this lesson yet.',
    // A refused delete is guidance: learner progress references the lesson.
    'lessons.deleteRefused': 'This lesson cannot be removed — learners have already ticked or annotated it. Edit the lesson instead.',
    'lessons.addContent': 'Add material',
    'lessons.editContent': 'Edit material',
    'lessons.kind': 'Kind',
    'lessons.body': 'Body',
    'lessons.url': 'URL',
    'lessons.urlMustBeAbsolute': 'Must be an absolute http(s) URL.',
    'lessons.uploadFile': 'Upload a file',
    'lessons.uploadHint': 'Or upload the file itself instead of linking to it.',
    'lessons.videoUploadOnly': "Videos must be uploaded. Linking to a video elsewhere isn't allowed: anyone with the link could watch or download it.",
    // artifact_unavailable is NOT an error: the deployment has no bucket.
    'lessons.uploadUnavailable': 'File storage is not set up on this deployment — link to the file by URL instead.',
    'lessons.uploading': 'Uploading… {pct}%',
    'lessons.uploaded': 'Uploaded',

    'assignment.title': 'Assignment',
    'assignment.create': 'Write the brief',
    'assignment.delete': 'Delete the brief',
    'assignment.brief': 'Brief',
    'assignment.files': 'Files handed out with this brief',
    'assignment.attachFile': 'Attach a file',
    'assignment.requirement': 'What learners hand in',
    'assignment.requirementPlaceholder': 'Choose what is handed in…',
    // Deliberately undecided: the value is the curriculum owner's, and the
    // form refuses to guess it.
    'assignment.requirementUndecided': 'There is no default — choose deliberately. This decides what the grading screen expects.',
    'assignment.emptyTitle': 'No assignment brief',
    'assignment.emptyHint': 'Without a brief, learners type into a submission box with no prompt attached to it.',
    'assignment.passMarkLivesOnModule': 'The pass mark lives on the module above (frozen with the version) — it is deliberately not repeated here.',

    'submissionRequirement.text': 'Written text',
    'submissionRequirement.link': 'A link',
    'submissionRequirement.file': 'An uploaded file',

    'cohortContent.modulePicker': 'Module',
    'cohortContent.moduleLayerHeading': 'From the module (every cohort sees this)',
    'cohortContent.readOnlyLabel': 'Read-only here',
    'cohortContent.moduleLayerEmpty': 'The module provides no material yet.',
    'cohortContent.cohortLayerHeading': 'This cohort’s own material',
    'cohortContent.addItem': 'Add material',
    'cohortContent.emptyTitle': 'Nothing added for this delivery',
    'cohortContent.emptyHint': 'Recordings, slides, meeting links and extra examples for this cohort go here — without touching what other cohorts teach from.',
    // Three DISTINCT read-only reasons — a delisted instructor shown a generic
    // "access denied" signs out and back in forever.
    'cohortContent.readOnlyNotYourCohort': 'Read-only: you are not the assigned instructor of this cohort.',
    'cohortContent.readOnlyDelisted': 'Read-only: your instructor standing is delisted. Re-certification restores authoring — signing out will not.',
    'cohortContent.readOnlyClosed': 'Read-only: this cohort is completed or cancelled, so its delivery content is a record now.',

    // ───────────────────────────────────── quiz authoring (phase 9)

    'quizKind.singleChoice': 'Single choice',
    'quizKind.multiChoice': 'Multiple choice',

    'quiz.title': 'Quizzes',
    'quiz.create': 'Add quiz',
    'quiz.emptyTitle': 'No quizzes yet',
    'quiz.emptyHint': 'A quiz is an ungraded self-check — auto-scored, and it can never reach a grade, a band or a credential.',
    'quiz.questionCount': '{count} questions',
    'quiz.attemptCount': '{count} attempts',
    'quiz.editorTitle': 'Quiz editor',
    'quiz.backToModule': 'Back to the module',
    'quiz.backToAssessment': 'Back to the assessment',
    'quiz.backToLesson': 'Back to the lesson',
    // The lock, named BEFORE a save fails: attempts pin the structure.
    'quiz.locked': 'This quiz has {count} recorded attempts, so its structure — questions, options, points — is locked. A recorded score whose questions changed is a number nobody can explain. Title, instructions, the attempt cap and the advisory mark stay editable.',
    'quiz.instructions': 'Instructions',
    'quiz.lesson': 'Lesson',
    'quiz.lessonHint': 'Where the quiz appears for learners.',
    'quiz.moduleLevelCheck': 'Module-level check (no specific lesson)',
    'quiz.maxAttempts': 'Attempt limit',
    'quiz.maxAttemptsHint': 'Blank = unlimited.',
    'quiz.passPct': 'Advisory mark (%)',
    // ADVISORY, spelled out: this must never read as a pass mark.
    'quiz.passPctAdvisory': 'Advisory only — shown to the learner as "you scored 60%, the mark is 70%". It gates nothing: quizzes never count toward grades, bands or certification. The real pass mark lives on the module.',
    'quiz.questions': 'Questions',
    'quiz.addQuestion': 'Add question',
    'quiz.newPrompt': 'New question — edit the prompt',
    'quiz.noQuestionsTitle': 'No questions yet',
    'quiz.noQuestionsHint': 'A quiz with no questions cannot be taken (quiz_unconfigured) — authoring is unfinished, nothing is broken.',
    'quiz.prompt': 'Prompt',
    'quiz.kind': 'Kind',
    'quiz.points': 'Points',
    'quiz.options': 'Options',
    'quiz.oneCorrect': 'exactly one correct',
    'quiz.manyCorrect': 'one or more correct',
    'quiz.markCorrect': 'Mark option {n} correct',
    'quiz.optionText': 'Option {n} text',
    'quiz.removeOption': 'Remove option {n}',
    // The lowercase form ReorderableList's own "Remove {item}" / "Move {item}
    // earlier" wrap around — keeps the reorder controls' aria-labels reading
    // the same way quiz.removeOption always did.
    'quiz.optionOrdinal': 'option {n}',
    'quiz.addOption': 'Add option',
    'quiz.needTwoOptions': 'A question needs at least two options.',
    'quiz.needACorrectOption': 'Mark at least one option correct.',
    'quiz.saveOptions': 'Save options',
    'quiz.saveQuestion': 'Save question',
    'quiz.deleteQuestion': 'Delete question',
    'quiz.questionLocked': 'Locked — {count} recorded attempts.',
    'quiz.delete': 'Delete quiz',
    'quiz.deleteHint': 'Deleting a quiz removes its questions and options. Possible only while no attempts exist.',
    'quiz.deleteLocked': 'This quiz has {count} recorded attempts and cannot be deleted — a recorded score whose quiz vanished is a number nobody can explain.',

    // ───────────────────────── free-text questions & marking (phase 2, AI plan)
    'quizKind.shortAnswer': 'Short answer',
    'quizKind.essay': 'Essay',
    'quiz.freeTextBadge': 'Free text',
    'quiz.gateBadge': 'Gate',
    'quiz.markingModeHint': 'Only free-text answers are marked; choice questions are scored automatically.',
    'quiz.markingGuide': 'Marking guide',
    'quiz.markingGuideHint': 'What a full answer contains. Used by the marker, never shown to learners.',
    'quiz.markingGuideRequired': 'A free-text question needs a marking guide before it can be saved.',
    'quiz.maxAnswerChars': 'Max answer length',
    'quiz.maxAnswerCharsHint': 'Between 50 and 10,000 characters.',

    // ───────────────────────────── AI authoring (phase 5, AI plan §8) — the
    // draft is never saved by these keys' own action; the curator's existing
    // Save is what persists anything.
    'aiDraft.generate': '✦ Generate with AI',
    'aiDraft.optionsTitle': 'Generate a quiz with AI',
    'aiDraft.reviewTitle': 'Review the draft',
    'aiDraft.questionCount': 'Number of questions',
    'aiDraft.kinds': 'Question kinds',
    'aiDraft.difficulty': 'Difficulty',
    'aiDraft.difficulty.intro': 'Intro',
    'aiDraft.difficulty.core': 'Core',
    'aiDraft.difficulty.stretch': 'Stretch',
    'aiDraft.notes': 'Notes for the AI (optional)',
    'aiDraft.notesHint': 'Guidance on topic or emphasis. Treated as data, never as an instruction that overrides the grounding rules.',
    'aiDraft.draftTitle': 'Title',
    'aiDraft.truncatedBanner': 'The lesson is long — only the first part of it was used to write this draft.',
    'aiDraft.droppedBanner': '{count} question(s) did not meet the quiz\'s rules and were left out.',
    'aiDraft.removeQuestion': 'Remove question {n}',
    'aiDraft.correct': 'Correct',
    'aiDraft.saveAsQuiz': 'Save as quiz',
    // ─── Screening (for HR) — SKILL-ASSESSMENT-PLAN.md §4 ───────────────────
    'screening.title': 'Screening (for HR)',
    'screening.intro': 'Tests HR sends to applicants and employees to measure one skill this module teaches — learners never see them, and only published ones are offered to HR.',
    'screening.newQuiz': 'New screening quiz',
    'screening.draftQuiz': 'Draft a quiz with AI',
    'screening.newTask': 'New practical task',
    'screening.editTask': 'Edit practical task',
    'screening.noSkills': 'Add this module’s skills and tools above first — every screening item measures one of them.',
    'screening.empty': 'No screening items yet. Create a quiz or a practical task for one of this module’s skills.',
    'screening.quizzes': 'Quizzes',
    'screening.tasks': 'Practical tasks',
    'screening.quizFacts': '{count} questions · {minutes} min',
    'screening.taskFacts': '{requirement} · {minutes} min',
    'screening.published': 'Published',
    'screening.draft': 'Draft',
    'screening.publish': 'Publish',
    'screening.unpublish': 'Unpublish',
    'screening.copy': 'Copy',
    'screening.copyHint': 'Make an editable draft copy — use this to change a quiz that already has attempts.',
    'screening.translate': 'Translate with AI',
    'screening.translateTitle': 'Translate this quiz',
    'screening.translateInto': 'Translate into',
    'screening.translateHint': 'AI drafts the translation with the same questions and answers. You review it, then it is saved as a new draft quiz in that language.',
    'screening.saveAsQuiz': 'Save as screening quiz',
    'screening.skill': 'Skill measured',
    'screening.skillHint': 'One of this module’s skills and tools — the same code HR uses for the skill.',
    'screening.level': 'Target level',
    'screening.levelHint': 'The questions should tell someone at this level from someone one level below.',
    'screening.levelName.1': '1 — Basic',
    'screening.levelName.2': '2 — Developing',
    'screening.levelName.3': '3 — Working',
    'screening.levelName.4': '4 — Advanced',
    'screening.levelName.5': '5 — Expert',
    'screening.language': 'Language',
    'screening.language.en': 'English',
    'screening.language.ar': 'Arabic',
    'screening.language.fr': 'French',
    'screening.minutes': 'Suggested time (minutes)',
    'screening.createAndAddQuestions': 'Create and add questions',
    'screening.draftBrief': 'Draft the brief with AI',
    'screening.draftBriefHint': 'Fills the title, brief and hand-in. Write the marking guide yourself.',
    'screening.brief': 'What the candidate is asked to do',
    'screening.briefHint': 'Shown to the candidate. Say exactly what to produce and hand in.',
    'screening.markingGuide': 'Marking guide',
    'screening.markingGuideHint': 'What a full answer contains. Only markers see this — never the candidate.',
    'screening.badge': 'Screening quiz (for HR)',
    'screening.editorHint': 'Not part of the course. One attempt, no pass mark — the score goes to HR as a skill level.',
    'screening.lockedHint': 'Locked: this quiz has attempts. Copy it to change the skill or language.',
    'screening.skillHintStandalone': 'Optional. A skill code for HR — not validated against any module.',
    'screening.skillPlaceholder': 'e.g. Revit, Dynamo, AutoCAD',
    'aiDraft.regenerate': 'Regenerate',
    'aiDraft.partialQuizCreated': 'A quiz was partially created before this failed.',
    'aiDraft.viewPartialQuiz': 'View the partial quiz',
    'aiDraft.briefModalTitle': 'Draft an assignment brief with AI',
    'aiDraft.useThisDraft': 'Use this draft',
    'aiDraft.suggestedChecks': 'Checks this brief exercises: {codes}',
    'aiDraft.noSuggestedChecks': 'No check codes yet — the draft will not suggest any.',

    // The learner's module screen (LearnModule) — the page the module-content
    // plan exists for. Two progress figures, never blended.
    'learn.title': 'Module',
    'learn.position': '{position} of {count}',
    'learn.credits': '{credits} credits',
    'learn.assessed': 'Assessed',
    'learn.recordedProgress': 'Recorded progress',
    'learn.selfReportedProgress': 'My own progress',
    'learn.selfReportedHint': 'Your own ticks — shown beside the recorded figure, never merged with it.',
    'learn.noLessonsYet': 'No lessons yet',
    'learn.bestScore': 'Best score {score}',
    // ── The lesson player (WS17a) ────────────────────────────────────────────
    // A lesson is now a PLACE, not a row of download buttons: a recording is
    // watched here, a note is written beside it and this lesson's quiz is
    // taken in the same screen.
    'learn.lessonTitle': 'Lesson',
    'learn.material': 'Material',
    'learn.noMaterialHint': 'Nothing has been attached to this lesson yet. Your instructor adds recordings, readings and links as the cohort runs.',
    'learn.readOnly': "You are viewing this learner's enrollment, not your own. Ticks, notes, quizzes and Next belong to the learner, so they are not available here.",
    'learn.lessonQuizzes': 'Quiz for this lesson',
    // MODULE-LEVEL only. A quiz that names a lesson renders inside that lesson;
    // what is left here is what genuinely comes after the whole module.
    'learn.moduleQuizzes': 'After this module',
    'learn.lessonNav': 'Lessons',
    'learn.backToModule': 'Back to the module',
    'learn.lessonMissingTitle': 'That lesson is not in this module',
    'learn.lessonMissingHint': 'It may have been removed from the curriculum since the link was made.',
    // SAID PLAINLY, because the alternative is a frozen picture and no
    // explanation. A signed URL lasts minutes and the player re-mints one
    // automatically; reaching this message means that already happened and the
    // file itself is the problem.
    // ── The learner hands in their work (WS17b) ─────────────────────────────
    // Before this the assignment section linked to the STAFF assessment flow,
    // which a learner cannot open — so the one thing the brief asked for was
    // the one thing the screen could not help with.
    'learn.yourSubmission': 'Your submission',
    'learn.yourAnswer': 'Your answer',
    'learn.answerPlaceholder': 'Write your answer here, attach your files below, or both.',
    'learn.attachFile': 'Attach a file',
    'learn.uploading': 'Uploading… {pct}%',
    'learn.uploadFailed': 'The file could not be sent to storage. If your connection is working, ask an administrator to check the storage settings.',
    'learn.saveDraft': 'Save draft',
    'learn.handItIn': 'Hand it in',
    // ONE-WAY, and the copy has to say so: only an instructor returning it
    // opens it again. A learner who presses expecting to keep editing has been
    // misled by the button, not by the server.
    'learn.handInWarning': 'Once you hand it in you cannot change it — your instructor has to return it first.',
    'learn.handedIn': 'Handed in. Your instructor has it.',
    'learn.handedInAt': 'Handed in on {when}. Your instructor has it.',
    'learn.returnedBecause': 'Your instructor sent this back: {reason}',
    // A FAILED PUT IS NEARLY ALWAYS THE BUCKET'S CORS POLICY, and the browser
    // reports it as an opaque network error with nothing in any server log.
    // Name the thing to check rather than a status code.
    'learn.uploadRefused': 'The file could not be uploaded. If this keeps happening, tell your instructor — the storage settings may need attention.',
    'submissionState.draft': 'Draft',
    'submissionState.awaitingInstructor': 'With your instructor',
    'submissionState.confirmed': 'Graded',
    'submissionState.returned': 'Sent back to you',
    'learn.lessons': 'Lessons',
    'learn.noLessonsTitle': 'No lessons yet',
    'learn.noLessonsHint': 'Material is being prepared for this module — nothing is missing on your side.',
    'learn.cohortExtras': 'From your cohort',
    'learn.cohortLayer': 'Your cohort',
    'learn.tick': 'Done',
    'learn.status.done': 'Done',
    'learn.status.liveOn': 'Live · {when}',
    'learn.status.held': 'Class held',
    'learn.status.quizPending': 'Quiz to take',
    'learn.status.quizPassed': 'Quiz passed',
    'learn.materialCount': 'Material: {count}',
    'learn.openLesson': 'Open lesson',
    'learn.materialList': 'Lesson material',
    'learn.opened': 'Opened',
    'learn.group.videos': 'Videos',
    'learn.group.documents': 'Documents',
    'learn.group.readings': 'Readings',
    'learn.group.files': 'Design files',
    'learn.group.links': 'Links',
    'learn.pdfFallback': 'If the document does not show here, download it.',
    'learn.steps': 'Lesson steps',
    'learn.step.attend': 'Attend the live class',
    'learn.step.attendHint': 'Done once your instructor marks the class held.',
    'learn.step.attendDone': 'The class has been held.',
    'learn.step.watch': 'Watch the material',
    'learn.step.watchHint': 'Marked done when you move on with Next.',
    'learn.step.watchDone': 'Watched.',
    'learn.step.complete': 'Work through the material',
    'learn.step.noQuiz': 'This lesson has no quiz.',
    'learn.step.next': 'Continue',
    'learn.step.doneLabel': 'Done',
    'learn.markComplete': 'Mark complete',
    'learn.fileOpensElsewhere': 'Opens in the program installed for it, such as Revit or AutoCAD.',
    'learn.download': 'Download',
    'learn.noMaterial': 'No material on this lesson yet.',
    'learn.openFile': 'Open file',
    'learn.openLink': 'Open',
    'learn.noteToggle': 'My note',
    'learn.noteTitle': 'My note',
    'learn.notePlaceholder': 'Private to you — instructors and staff never see it.',
    'notes.aiSummaryButton': '✦ Summarise lesson into my note',
    'notes.aiSummaryTitle': 'Draft key points — not saved yet',
    'notes.aiSummaryInsert': 'Insert into my note',
    'notes.aiSummaryDiscard': 'Discard',
    'tutor.title': 'Ask about this lesson',
    'tutor.askPlaceholder': 'Ask a question about this lesson…',
    'tutor.send': 'Ask',
    'tutor.thinking': 'Thinking…',
    'tutor.remaining': '{count} questions left today',
    'tutor.ungrounded': 'Not covered by this lesson — ask your instructor',
    'tutor.clearHistory': 'Clear history',
    'tutor.clearConfirm': 'Clear all questions on this lesson?',
    'learn.quizzes': 'Self-checks',
    'learn.noQuizzesTitle': 'No self-checks',
    'learn.noQuizzesHint': 'This module has no quizzes — they are optional practice, not a requirement.',
    'learn.quizBest': 'Best {score}%',
    'learn.attemptsOf': '{used} of {max} attempts',
    'learn.attemptsUnlimited': '{used} attempts',
    'learn.takeQuiz': 'Take',
    'learn.retakeQuiz': 'Retake',
    'learn.quizAttemptsExhausted': 'You have used all your attempts. Re-watch the lesson material and try again.',
    'learn.restartLesson': 'Restart lesson',
    'learn.quizUnconfiguredTitle': 'Not ready to take yet',
    'learn.quizUnconfiguredHint': 'This self-check is still being authored — nothing on your side is wrong.',
    'learn.quizAdvisoryMark': 'The mark is {pct}% — advisory only; this never counts toward your grade.',
    'learn.submitAttempt': 'Submit',
    'learn.attemptNumber': 'Attempt {n}',
    'learn.quizScoredAgainstMark': 'You scored {score}% — the mark is {pct}%.',
    'learn.questionCorrect': 'Correct',
    'learn.questionIncorrect': 'Incorrect',
    'learn.questionN': 'Question {n}',
    // ── Free-text quiz answers and marking (ACADEMY-AI-WORKFLOWS-PLAN §6.5) ──
    'learn.quizAwaitingMarking': 'Awaiting marking',
    'learn.viewFeedback': 'View feedback',
    'learn.quizAwaitingAiMark': 'Submitted — being marked by AI. This usually takes under a minute.',
    'learn.quizAwaitingInstructorMark': 'Submitted — your instructor will mark your written answers.',
    'learn.canContinueToNextLesson': 'You can continue to the next lesson.',
    'learn.charsRemaining': '{count} characters left',
    'learn.markedByAi': 'Marked by AI',
    'learn.markedByInstructor': 'Marked by your instructor',
    'learn.pointsAwarded': '{points} of {max} points',
    'learn.assignment': 'Assignment',
    'learn.noAssignmentTitle': 'No assignment',
    'learn.noAssignmentHint': 'This module has no assignment brief yet.',
    'learn.handIn': 'Hand in: {what}',
    'learn.locked': 'Locked',
    'learn.lockedByQuiz': 'Pass the check on “{lesson}” to open this.',
    'learn.lockedBySessionDated': 'Opens after the live session on “{lesson}”, {when}.',
    'learn.lockedBySessionUndated': 'Opens after the live session on “{lesson}”. No date has been set yet.',
    'learn.moduleLocked': 'Module {code} has to be passed before this one opens. You can read ahead, but your ticks will not count yet.',
    'learn.liveLesson': 'Live session',
    'learn.liveLessonHint': 'This lesson is a live class with your cohort; its material is below.',
    // PAST OR FUTURE. The relative formatter gives "in 10 minutes" or "10 minutes
    // ago", but the sentence around it needs the right verb tense. SessionCard
    // chooses the key based on whether startsAt is before or after now.
    'learn.sessionStarts': 'Starts {when}',
    'learn.sessionStarted': 'Started {when}',
    'learn.sessionDuration': '{minutes} minutes',
    'learn.sessionJoin': 'Join the session',
    'learn.sessionHeld': 'This session has been held.',
    'learn.sessionNotScheduled': 'Your instructor has not set a date for this session yet.',
    'learn.sessionRecording': 'Recording',
    'learn.liveNow': 'Live now',
    'learn.sessionEnter': 'Enter the classroom',
    'learn.sessionOpensAt': 'The classroom opens at {when}.',
    'learn.sessionCancelled': 'This session was cancelled. Your instructor will schedule another.',
    'learn.sessionRecorded': 'This session was recorded.',
    'learn.watchRecording': "Watch the recording",
    'learn.videoNotUploaded': "This video hasn't been uploaded to the Academy yet, so it can't be played here. Ask your instructor.",
    'learn.mediaRetry': "Try again",
    'learn.play': "Play",
    'learn.hidePlayer': "Hide player",
    'learn.fullScreen': "Full screen",
    'learn.exitFullScreen': "Exit full screen",
    'learn.mediaUnavailable': "This recording would not play. Try again, or tell your instructor if it keeps happening.",
    'learn.sessionEnded': 'This session has ended.',
    'learn.liveConnecting': 'Connecting you to the classroom…',
    'learn.liveEmbedFailed': 'The classroom could not be loaded.',
    'learn.liveRetry': 'Try again',
    // ── The "Next" button (ACADEMY-AI-WORKFLOWS-PLAN §9.4) — server-decided,
    // not computed from the lesson list any more.
    'learn.next.nextQuiz': 'Next: {title}',
    'learn.next.nextLesson': 'Next lesson: {title}',
    'learn.next.nextLessonGeneric': 'Next lesson',
    'learn.next.moduleAssessment': 'Next: Module assessment',
    'learn.next.awaitingAssessmentMark': 'Assessment with your instructor',
    'learn.next.continueToNextModule': 'Continue to next module',
    'learn.next.nextModule': 'Next module: {title}',
    'learn.next.programmeComplete': 'Programme complete',
    'learn.next.goToMyLearning': 'Go to My Learning',
    // A hint next to the recorded CompletionState when it was written
    // automatically (D7) rather than by an instructor.
    'learn.completedAutomatically': 'Completed automatically',
    'cohort.tabSessions': 'Sessions',
    'cohortSessions.module': 'Module',
    'cohortSessions.schedule': 'Schedule a session',
    'cohortSessions.noneTitle': 'No sessions scheduled',
    'cohortSessions.noneHint': 'Live lessons in this module need a date before learners can attend — and before the lessons behind them open.',
    'cohortSessions.state.scheduled': 'Scheduled',
    'cohortSessions.state.held': 'Held',
    'cohortSessions.state.cancelled': 'Cancelled',
    'cohortSessions.gatesNothing': 'Opens nothing',
    'cohortSessions.markHeld': 'Mark held',
    'cohortSessions.markHeldHint': 'Opens every lesson waiting on this session, for the whole cohort.',
    'cohortSessions.reopen': 'Mark not held',
    'cohortSessions.reschedule': 'Reschedule',
    'cohortSessions.rescheduleHint': 'The cohort is told the new time and gets its reminder again.',
    'cohortSessions.saveNewTime': 'Save the new time',
    'cohortSessions.lessonScheduledOn': '{lesson} — already scheduled for {when}',
    'cohortSessions.lessonHeldOn': '{lesson} — already held on {when}',
    'cohortSessions.closeOut.question': 'Did this class finish?',
    'cohortSessions.closeOut.hint': 'Until you answer, the lessons after it stay locked for the cohort, and a recording alone does not count because it also stops when a connection drops.',
    'cohortSessions.closeOut.done': 'Yes, it finished',
    'cohortSessions.closeOut.continuing': 'I got disconnected — continuing now',
    'cohortSessions.closeOut.redo': 'It needs to be redone',
    'cohortSessions.closeOut.continuingUntil': 'The classroom stays open until {when}. You will be asked again after that.',
    'cohortSessions.closeOut.awaitingNewDate': 'Marked to be redone. Reschedule it, or mark it held if it did finish.',
    'cohortSessions.cancel': 'Cancel session',
    'cohortSessions.opensLessons': 'Lessons waiting on this session are now open for the cohort.',
    'cohortSessions.titleField': 'Title',
    'cohortSessions.lesson': 'Lesson',
    'cohortSessions.noLesson': 'No lesson — a clinic or office hours',
    'cohortSessions.noLiveLessons': 'This module has no live lessons, so a session here opens nothing. That is fine for office hours.',
    'cohortSessions.startsAt': 'Starts at',
    'cohortSessions.duration': 'Duration (minutes)',
    'cohortSessions.joinUrl': 'Join link',
    'cohortSessions.joinUrlHint': 'Paste the link from your conferencing tool. It is shown to the cohort and never opened by the Academy.',
    'cohortSessions.delivery': 'How it meets',
    'cohortSessions.deliveryPasted': 'An external link',
    'cohortSessions.deliveryIntegrated': 'In the Academy',
    'cohortSessions.deliveryIntegratedHint': 'The class runs inside the lesson page.',
    'cohortSessions.deliveryUnavailable': 'Conferencing is not configured on this deployment, so only an external link is available.',
    'cohortSessions.joinBefore': 'Doors open (minutes before)',
    'cohortSessions.joinBeforeHint': 'How early learners may enter. Enforced by the server, not just on screen.',
    'cohortSessions.integratedBadge': 'In the Academy',
    'cohortSessions.enter': 'Enter as host',
    'nextClass.title': 'Next class',
    'nextClass.none': 'No class of yours in the next two weeks.',
    'nextClass.openLink': 'Open meeting link',
    'nextClass.manage': 'Manage session',
    'cohortSessions.attachRecording': 'Attach the recording',
    'cohortSessions.changeRecording': 'Change the recording',
    'cohortSessions.recording': 'Recording',
    'cohortSessions.noRecording': 'None',
    'cohortSessions.noRecordings': 'No video has been uploaded to this cohort for this module yet. Add it on the Content tab first.',
    'engagement.waitingOn': 'Waiting on',
    'engagement.waitingQuiz': 'The check on “{lesson}”',
    'engagement.waitingSession': 'The live session on “{lesson}”',
    'lessons.requirement': 'How it is delivered',
    'lessons.requirementSelfPaced': 'Self-paced — material the learner works through alone',
    'lessons.requirementSynchronous': 'Live session — the cohort meets for it',
    'lessons.requirementSelfPacedHint': 'Carries recordings, readings and links. Learners work through it at their own pace.',
    'lessons.requirementSynchronousHint': 'Every cohort teaching this module schedules this class, and the lessons after it stay shut until it is held; any material shows under the session.',
    'lessons.liveBadge': 'Live session',
    'lessons.sessionSpecHint': 'This lesson IS the session — it carries no material. Schedule it on the cohort’s Sessions tab.',
    'lessons.sessionObjectives': 'What the session covers',
    'lessons.sessionObjectivesHint': 'The agenda for the gathering. The same in every cohort — only the date differs.',
    'lessons.sessionPreparation': 'What learners bring',
    'lessons.sessionPreparationHint': 'Shown on the session card before the class, which is the only moment it helps.',
    'lessons.expectedDuration': 'Expected duration (minutes)',
    'lessons.expectedDurationHint': 'Pre-fills the schedule form. An instructor may run it longer or shorter.',
    'lessons.requiresAttendance': 'Attendance',
    'lessons.attendanceOptional': 'Optional — the session opens the next lessons for everyone',
    'lessons.attendanceRequired': 'Required to continue',
    'lessons.attendanceRequiredHint': 'The lessons after this one open only for learners marked present, or for everyone once the class has a recording.',
    'lessons.requiresAttendanceOnHint': 'The lessons after this one open PER LEARNER, from the register — a missed class cannot be re-attended, so correct the register or schedule a repeat.',
    'lessons.requiresAttendanceOffHint': 'Marking the session held opens the next lessons for the whole cohort at once.',
    'lessons.quizMarking': 'Quiz free-text answers marked by',
    'lessons.quizMarkingHint': 'Choice questions are always scored automatically. This decides who marks written answers.',
    'lessons.quizMarkingAiBadge': 'Marked by AI',
    'lessons.quizMarkingInstructorBadge': 'Marked by instructor',
    'markingMode.instructor': 'Instructor',
    'markingMode.ai': 'AI',
    'markingMode.assessmentAi': 'AI (drafts a score; an instructor always confirms)',
    'markingMode.aiNotConfigured': 'AI marking is not configured.',
    'learn.lockedByAttendance': 'You are not marked as having attended {lesson}. Ask your instructor — they can correct the register or run the session again.',
    'cohortSessions.register': 'Register',
    'cohortSessions.registerHint': 'Everyone is marked present when you hold the session. Untick whoever was not there.',
    'cohortSessions.present': 'Present',
    'cohortSessions.saveRegister': 'Save the register',
    'cohortSessions.registerSaved': 'Register saved.',
    'cohortSessions.noRoster': 'Nobody is enrolled on this cohort yet.',
    'learn.openAssessmentFlow': 'Open my enrollment record',

    // The instructor's Engagement tab on CohortDetail (phase 7). Two
    // progress columns, never merged.
    'engagement.statEnrolled': 'Enrolled',
    'engagement.statActive': 'Active',
    'engagement.statFinished': 'Finished',
    'engagement.modulePicker': 'Module',
    'engagement.progress': 'Progress',
    'engagement.learner': 'Learner',
    'engagement.recorded': 'Recorded',
    'engagement.selfReported': 'Self-reported',
    'engagement.noLessons': 'No lessons yet',
    'engagement.noEnrollments': 'No enrollments',
    'engagement.noEnrollmentsHint': 'Nobody is enrolled in this cohort yet.',
    'engagement.loadMore': 'Load more',
    'engagement.quizzes': 'Self-checks',
    'engagement.quizPicker': 'Self-check',
    'engagement.noQuizzes': 'No self-checks on this module',
    'engagement.noQuizzesHint': 'Quizzes are authored on the module screen; results appear here once learners take them.',
    'engagement.noAttempts': 'No attempts yet',
    'engagement.noAttemptsHint': 'Once learners take this self-check, the per-question correct rate shows which topics landed.',
    'engagement.noAttemptsShort': 'no attempts',
    'engagement.correctRate': '{pct}% correct',
    'engagement.attempts': 'Attempts',
    'engagement.best': 'Best',
    'engagement.state': 'State',
    'engagement.quizStateAwaiting': 'Awaiting marking',
    'engagement.quizStateMarkedByAi': 'Marked by AI',
    'engagement.quizStateMarkedByInstructor': 'Marked by instructor',
    'engagement.lastAttempt': 'Last attempt',
    'engagement.submissions': 'Submissions',
    'engagement.noSubmissions': 'No submissions',
    'engagement.noSubmissionsHint': 'Assessments opened for this cohort appear here with their states.',
    'engagement.fileMissing': 'File required, none attached',
    'engagement.tutorQuestions': 'Tutor questions',
    'engagement.tutorQuestionsHint': "Lessons whose content didn't cover what learners asked.",
    'engagement.tutorLesson': 'Lesson',
    'engagement.tutorQuestionsCount': 'Questions',
    'engagement.tutorNotCovered': 'Not covered',
    'engagement.noTutorQuestionsTitle': 'No tutor questions yet',
    'engagement.noTutorQuestionsHint': 'Once learners ask the lesson tutor, their questions are counted here.',
    // ─── Row actions (§6g.2) ───────────────────────────────────────────────
    'engagement.actions': 'Actions',
    'engagement.message': 'Message',
    'engagement.messageTitle': 'Message {name}',
    'engagement.messageHint': 'The only channel this app has to them: their own email address.',
    'engagement.noEmail': 'No email on file for this person.',

    // ─── Public landing page ─────────────────────────────────────────────────
    // The UI vocabulary of the anonymous marketing page: buttons, labels, aria
    // strings. THE PROSE IS NOT HERE — hero copy, pillar paragraphs, outcomes,
    // resources, instructor and portfolio cards live in
    // src/content/landing/{en,ar,fr}.js, because they are multi-line paragraphs
    // and nested arrays and this dictionary is flat key => short string.
    //
    // The line: if it is a control the user operates, it is here. If it is
    // something the user reads, it is in the content file.
    // THE PUBLIC PAGES' OWN NAME, on the landing header and footer lockups
    // only. `app.name` stays the product's name inside the application; the
    // marketing site is sold as the VDC Academy.
    'landing.brandName': 'VDC Academy',
    'landing.skipToContent': 'Skip to content',
    'landing.sectionsNavLabel': 'Page sections',
    'landing.openMenu': 'Open menu',
    'landing.closeMenu': 'Close menu',
    // For a signed-in visitor every call to action becomes this instead.
    'landing.goToPortal': 'Go to portal',
    'landing.cta.primary': 'Request a Custom Training Programme',
    'landing.cta.secondary': 'Explore the Curriculum',
    'landing.cta.plan': 'Enquire',
    'landing.pillars.modules': '{count} modules',
    'landing.pillars.modulesOne': '1 module',
    'landing.framework.level': 'Level {ordinal}',
    'landing.curriculum.noModules': 'Modules for this level are being finalised.',
    'landing.footer.linksTitle': 'Quick links',
    'landing.backToTop': 'Back to top',
    // The contact form. It posts to's public intake — the one funnel,
    // and now the only one: the Academy's own intake was deleted with the
    // admission queue. See screens/landing/sections/ContactCta.jsx.
    'landing.form.name': 'Full name',
    'landing.form.email': 'Work email',
    'landing.form.organisation': 'Organisation',
    'landing.form.phone': 'Phone',
    'landing.form.programmes': 'Programmes you are interested in',
    'landing.form.programmesHint': 'Optional — tick the programmes you would like us to quote for.',
    'landing.form.role': 'Your role',
    'landing.form.country': 'Country',
    // A PICKER, not a text box: the Academy resolves a converted lead's country
    // through its market list, so anything typed here reached nothing. The hint
    // is what keeps that honest — somebody outside the markets is still a lead
    // worth having, and the message field is where they say so.
    'landing.form.countrySelect': 'Select a country…',
    'landing.form.countryHint': 'So we route your enquiry to the right team.',
    'landing.form.seats': 'Seats of interest',
    'landing.form.seatsUnspecified': 'Not sure yet',
    'landing.form.updates': 'Keep me updated by email about new programmes and cohort dates. You can unsubscribe at any time.',
    'landing.form.submit': 'Send enquiry',
    'landing.form.acknowledged': 'Thank you — your enquiry has reached our team. We will reply to the address you gave. Nothing is created for you until we have spoken.',
    // ── Shared by BOTH public forms ──────────────────────────────────────────
    // Get-in-touch and /register are one funnel into the same endpoint, so they
    // fail the same way and say so in the same words.
    //
    // A person must never be thanked for an enquiry that did not arrive: these
    // say plainly that it did not, and what to do next.
    'intake.sending': 'Sending…',
    'intake.failed': 'Your enquiry did not reach us and nothing was saved. Please try again in a moment.',
    'intake.failedThrottled': 'Too many enquiries from this connection just now. Please wait a minute and send it again.',
    // ── The CAPTCHA, in the visitor's terms and never in Cloudflare's ────────
    // "Turnstile" and "token" mean nothing to somebody evaluating a training
    // offering. What they need to know is which of the two things on this form
    // is stopping them, and that nothing they typed is wrong.
    'intake.captchaPending': 'Complete the check above to send your enquiry.',
    'intake.captchaUnavailable': 'The security check could not load. A browser extension or an ad blocker is the usual reason; disable it for this page, or write to us instead.',
    'intake.failedCaptcha': 'The security check expired before your enquiry was sent. Nothing was lost — please complete it again and resend.',
    // NotFound links home, and "home" is two places now: the marketing page for
    // a visitor, the dashboard for a signed-in user. `common.backHome` covers the
    // second; this covers the first.
    'landing.backToSite': 'Back to the home page',
    // ── The Academy's own arrivals: memberships bought on the public page ──
    // NOBODY VOUCHED FOR THESE. A arrival was converted by a salesperson
    // and an HRM one was assigned by an employer; this one is a stranger who
    // filled in a checkout. So the row carries an ORDER and no person, and
    // admitting it — creating the customer — is a coordinator's act with its
    // own button. The wording says "quoted" everywhere money appears: no
    // payment provider stands behind the page.
    'arrivals.academy': 'Academy orders',
    'arrivals.order': 'Order',
    'arrivals.quoted': 'Quoted, not charged',
    'arrivals.admit': 'Admit',
    'arrivals.admitted': '{name} is now in the register. Issue their sign-in below.',
    'arrivals.awaitingAdmission': 'Admission failed — admit by hand',
    'arrivals.academyEmpty': 'No memberships have been bought on the public page yet.',
    'arrivals.academyLead': 'Bought on the public page. The buyer is already in the register — verify who they are, then issue their sign-in.',

    // ─── Membership and the mock checkout ────────────────────────────────────
    // THE CHECKOUT TAKES NO MONEY. There is no payment provider behind
    // /subscribe — it records an order in the Academy's arrivals queue and a
    // coordinator finishes it by hand. Every string below is written so that a
    // buyer cannot come away believing they were charged or that a login is
    // waiting for them. See screens/subscribe/Checkout.jsx.
    'landing.membership.talkToUs': 'Talk to a coordinator',
    'checkout.back': 'Back to the site',
    'checkout.title': '{tier} membership',
    'checkout.otherTiers': 'Other memberships',
    'checkout.seats': 'Seats',
    'checkout.oneSeatOnly': 'One seat — an individual membership is for the person who holds it.',
    'checkout.seatsBounds': 'Between {min} and {max} seats',
    'checkout.volumeBreak': '{seats}+ seats: {percent}% off',
    'checkout.programmes': 'Programmes',
    'checkout.subjects': 'Subjects',
    'checkout.allSubjects': 'All subjects',
    'checkout.selectAll': 'Select all',
    'checkout.clearAll': 'Clear',
    'checkout.perSeat': '{price} per seat',
    'checkout.pickProgramme': 'Choose at least one programme — a seat costs the programmes it covers.',
    'checkout.noProgrammesForTier': 'No programmes are on sale with this membership yet. Get in touch and we will quote you by hand.',
    'checkout.programmesHint': 'Each programme you tick adds its price to every seat. A coordinator confirms the fit before anyone is placed.',
    'checkout.details': 'Who is buying',
    'checkout.detailsHint': 'A coordinator verifies these before your sign-in is issued, so a reachable phone number is worth giving.',
    'checkout.summary': 'Your order',
    'checkout.seatsLine': '{seats} × {price}',
    'checkout.volumeDiscount': 'Volume discount ({seats}+ seats, {percent}%)',
    'checkout.promoDiscount': 'Promotion {code} ({percent}%)',
    'checkout.vat': 'VAT ({percent}%)',
    'checkout.pricing': 'Working out your total…',
    'checkout.priceUnavailable': 'We could not price this order just now. Please try again in a moment.',
    'checkout.redirectNotice': 'You will be taken to our payment provider to pay securely. Your membership starts once they confirm the payment — we never see your card details.',
    'checkout.pay': 'Pay {total}',
    'checkout.paying': 'Taking you to payment…',
    'checkout.failed': 'Your order did not reach us and nothing was charged. Please try again in a moment.',
    'checkout.paymentsUnavailable': 'Payments are unavailable right now and nothing was charged. Please get in touch and we will take your order by hand.',
    // ── Proving the mailbox, between the form and the payment page.
    'checkout.verify.title': 'Check your email',
    'checkout.verify.body': 'We sent a six digit code to {email}. Enter it to continue to payment.',
    'checkout.verify.label': 'Verification code',
    'checkout.verify.submit': 'Continue to payment',
    'checkout.verify.checking': 'Checking…',
    'checkout.verify.resend': 'Send a new code',
    'checkout.verify.resent': 'A new code is on its way.',
    'checkout.verify.wrong': 'That code is not right, or it has expired. Check the latest email, or ask for a new code.',
    'checkout.verify.locked': 'Too many attempts with that code. Ask for a new one.',
    'checkout.verify.resendLimit': 'A code was sent a moment ago. Wait a minute before asking again — and after three codes please start the order again.',
    'checkout.verify.expired': 'This order has expired and nothing was charged. Please start again.',
    'checkout.confirmedTitle': 'Payment confirmed',
    'checkout.orderRef': 'Order reference',
    'checkout.total': 'Total',
    // ── The return page. Four situations, and the WAIT IS NOT ONE OF THE BAD
    // ONES: the redirect and the provider's confirmation race, so "we are
    // waiting" is the ordinary opening state and the copy never apologises for
    // it or implies something went wrong.
    'checkout.returnWaitingTitle': 'Waiting for your payment',
    'checkout.returnWaitingLead': 'Your bank is confirming the payment. This usually takes a few seconds — you can leave this page open.',
    'checkout.returnStillWaitingLead': 'We have not heard back yet, which does not mean anything went wrong — confirmations can take a few minutes. Keep your order reference; we will email you as soon as it lands.',
    'checkout.returnPaidLead': 'Thank you — your membership is paid for. Keep your order reference; it is how we find you.',
    'checkout.returnClosedTitle': 'This order is closed',
    'checkout.returnClosedLead': 'The quote for this order ran out or it was cancelled, so nothing has been charged against it. Start again from the memberships page and we will re-quote you.',
    'checkout.returnRefundedTitle': 'This order was refunded',
    'checkout.returnRefundedLead': 'The payment for this order has been returned. If that is not what you expected, get in touch and quote your order reference.',
    'checkout.returnUnknownTitle': 'We cannot find that order',
    'checkout.returnUnknownLead': 'The link may have been shortened or mistyped. If you have an order reference, get in touch and quote it — nothing is lost.',
    'checkout.promo': 'Promotion code',
    'checkout.promoPlaceholder': 'Enter a code',
    'checkout.promoApply': 'Apply',
    'checkout.promoApplied': '{code} applied',
    'checkout.promoUnknown': 'That code is not one we recognise.',
    'checkout.emailNotice': 'A coordinator will verify your details and email you your sign-in. Nothing else is needed from you in the meantime — there is no account to set up yet.',

    // The renewal page — screens/subscribe/Renew.jsx. Where the renewal
    // letter sends a member; it asks the Academy what the order is and, on
    // a click, for the provider's page. Nothing is charged unattended.
    'renew.title': 'Renew your membership',
    'renew.lead': 'Your membership term is ending. Renew it for another term at the price below — the same plan and the same seats.',
    'renew.loadingTitle': 'Finding your renewal…',
    'renew.loadingLead': 'One moment while we look up the order in your letter.',
    'renew.paidTitle': 'Your membership is renewed',
    'renew.paidLead': 'This renewal has been paid and your term has been extended. Nothing else is needed from you.',
    'renew.closedTitle': 'This renewal offer has closed',
    'renew.closedLead': 'The price in your letter was held until a date that has passed, so nothing has been charged against it. Get in touch and quote your order reference and we will renew you by hand.',
    'renew.unknownTitle': 'We cannot find that renewal',
    'renew.unknownLead': 'The link may have been shortened or mistyped. If you have an order reference, get in touch and quote it — nothing is lost.',
    'renew.plan': 'Membership',
    'renew.termEnds': 'Current term ends',
    'renew.renewsUntil': 'Renewed through',
    'renew.priceHeldUntil': 'Price held until',
    'renew.nothingCharged': 'Nothing is charged unless you choose to renew. We hold no card on file — if you do nothing, your membership simply ends on the date above and you will not be billed.',
    'renew.failed': 'We could not open the payment page just now and nothing was charged. Please try again in a moment.',

    // ─── Company (B2B) accounts ───────────────────────
    // TWO AUDIENCES SHARE THIS PREFIX. `company.*` on /company is the
    // CUSTOMER's own words — they are not staff and never see the rest of the
    // app — and the same keys on /companies are the staff view of the same
    // accounts.
    //
    // "Seat" MEANS AN ENROLLMENT throughout, not a person. A company buys
    // twenty seats and spends one each time somebody is put into a cohort;
    // withdrawing them returns it. Any wording that makes a seat sound like a
    // headcount is wrong here, however natural it reads.
    'nav.company': 'My company',
    'nav.companies': 'Companies',

    'company.title': 'Company',
    // A university is a company account with a kind (PLATFORM-PLAN-5 decision
    // 6): same portal, same seats, and only the word above it changes.
    'company.titleUniversity': 'University',
    'company.subtitle': 'Your seats, your learners, and the cohorts you have been sold.',
    'company.noAccount': 'No company is linked to your account',
    'company.noAccountHint': 'Your Academy record is not attached to a company, so there are no seats to show — ask your Academy contact to attach it.',

    'company.seatsBought': 'Seats bought',
    'company.seatsUsed': 'Seats used',
    'company.seatsLeft': 'Seats left',
    'company.seatsLeftHint': 'A seat is spent by enrolling and returned by withdrawing.',
    'company.allowance': 'Seat allowance',
    'company.allowanceSpent': 'Every seat you bought is in use. Withdraw a learner from a cohort to free one, or talk to your Academy contact about buying more.',
    'company.learners': 'Learners',
    'company.ourLearners': 'Yours',

    'company.cohorts': 'Your cohorts',
    'company.noCohorts': 'No cohorts yet',
    'company.noCohortsHint': 'Cohorts appear here once the Academy has scheduled one against your account.',

    'company.noLearners': 'Nobody has been added yet',
    // REWRITTEN for WS20c: adding people is theirs to do now, and the old hint
    // told them to wait for somebody else.
    'company.noLearnersHint': 'Add the people you want to train. Naming somebody costs no seat — a seat is spent when you enrol them.',
    // ── PEOPLE, NOT SEATS (WS20d) ───────────────────────────────────────────
    // The four tiles used to describe the same quantity twice. Three are about
    // ENROLMENTS; this one is the roster a company can draw on. The hint is
    // what stops it being read as a spend.
    'company.people': 'People',
    'company.peopleHint': 'Named on your account. Naming costs no seat.',
    // The buyer, shown once in the header rather than among the people the
    // seats are for. "Account holder" rather than "administrator": it is what
    // they are to the Academy, not a permission level.
    'company.accountHolder': 'Account holder:',
    'company.accountHolderAlsoLearner': 'also enrolled',
    'company.addPerson': 'Add a person',
    'company.addPersonHint': 'They can be enrolled once added. Adding somebody spends no seat — a seat is spent when you enrol them into a cohort.',
    'company.signIn': 'Sign-in',
    'company.hasSignIn': 'Issued',
    'company.loginIssuedBy': 'Issued by {name} on {date}',
    // SHOWN ONCE AND NOWHERE ELSE. The wording has to carry that: a manager who
    // dismisses this panel without passing the password on cannot get it back,
    // and the recovery is the person using "Forgot password?" themselves.
    'company.seatsHeld': 'Seats held',
    'company.seatsHeldCount': '{count} seat(s)',
    'company.lastEnrolled': 'Last enrolled',
    'company.enrollable': 'Can be enrolled',
    // Somebody attached to the account before a learner profile exists for
    // them. Saying it on the row is cheaper than a refusal at submit time.
    'company.notEnrollable': 'Not yet',
    'company.issueLogin': 'Ask for a sign-in',
    'company.signInRequestedFor': 'A sign-in was requested for {name}.',
    'company.signInRequestedHint': 'They will be emailed a link to set their own password. Nobody here sees it.',
    'company.signInRequested': 'A sign-in was requested. They will be emailed a link to set their own password.',
    'company.loginIssued': 'Can sign in',
    'company.organisation': 'Organisation',
    'company.organisationHint': 'You manage more than one organisation. Choose which one to look at.',
    'company.manager': 'Manager',
    'company.managerAria': 'Make {name} a manager',
    'company.managerHint': 'A manager opens the company portal to that person. Only an Academy administrator decides who manages an organisation.',
    // ─── The portal's own cockpit ────────────────────────────────────────────
    // NINE SECTIONS, AND THE WORDS ARE THE CUSTOMER'S, NOT THE SCHEMA'S. This
    // reader has never seen an enrollment, a cohort request or a programme bar
    // named as such, so the labels say what the thing is to THEM: seats they
    // are spending, dates they asked for, whether their people are ready.
    //
    // THE SEAT/PERSON DISTINCTION SURVIVES EVERY LINE HERE. A seat is spent by
    // enrolling and returned by withdrawing; naming somebody costs nothing. Any
    // wording that makes a seat sound like a headcount is wrong, however
    // natural it reads.
    'nav.group.company': 'My company',
    'nav.companyProgrammes': 'Programmes',
    'nav.companyProgrammes.what': 'What you were sold',
    'nav.companyCohorts': 'Cohorts',
    'nav.companyCohorts.what': 'When your training runs',
    'nav.companyPeople': 'People',
    'nav.companyPeople.what': 'Who you can send',
    'nav.companyEnrollments': 'Seats in use',
    'nav.companyEnrollments.what': 'Who is on which cohort',
    'nav.companyRequests': 'Requests',
    'nav.companyRequests.what': 'Ask us to run one',
    'nav.companyLearning': 'Learning',
    'nav.companyLearning.what': 'How the modules are going',
    'nav.companyReadiness': 'Readiness',
    'nav.companyReadiness.what': 'Who is ready, and how ready',
    'nav.companyAttendance': 'Attendance',
    'nav.companyAttendance.what': 'Who turned up',

    // The overview's signal cards. Each is an ORDINARY state of an account
    // being used, not an error — the claim is only that it is worth a look, and
    // each one links to the section that acts on it.
    'company.signals': 'Worth a look',
    'company.signal.notStarted': 'have not started',
    'company.signal.notStartedHint': 'These seats are paid for and untouched — not one lesson, not one attempt.',
    'company.signal.notStartedCta': 'See who',
    'company.signal.noSignIn': 'cannot sign in yet',
    'company.signal.noSignInHint': 'They are on your account but have no sign-in, so they cannot see anything you bought.',
    'company.signal.noSignInCta': 'Issue sign-ins',
    'company.signal.openRequests': 'with us',
    'company.signal.openRequestsHint': 'Cohort requests we have not answered yet.',
    'company.signal.openRequestsCta': 'See your requests',
    'company.signal.allowanceSpent': 'seats, all in use',
    'company.signal.allowanceSpentCta': 'See who holds them',

    'company.activity': 'Activity',
    'company.modulesPassed': 'Modules passed',
    'company.modulesPassedHint': 'Across everyone currently enrolled.',
    'company.modulesInProgress': 'Modules under way',
    'company.credentialsHeld': 'Certificates held',
    'company.credentialsHeldHint': 'Active on the public register.',
    'company.attendanceRate': 'Attendance',
    'company.attendanceRateHint': 'Across classes with a register taken.',
    // NULL IS NOT ZERO, and this is where that costs the most: an account with
    // no register taken yet has not been asked, and a 0% would tell a company
    // its people never turn up.
    'company.attendanceUnknown': 'No register taken yet',
    // ─── Hours (PLATFORM-PLAN-3 §1c.2) ───────────────────────────────────────
    // DELIVERED AND SCHEDULED ARE TWO NUMBERS and the labels keep them apart: a
    // plan is not a delivery, and a customer must never be shown training they
    // have not had. ATTENDED is theirs alone — the hours their own people sat
    // through, from the register.
    'company.hoursDelivered': 'Hours delivered',
    'company.hoursDeliveredHint': 'Across {held} classes held for you.',
    'company.hoursAttended': 'Hours attended',
    'company.hoursAttendedHint': 'Sat through by your own people.',
    'company.hoursScheduled': 'Hours scheduled',
    'company.hoursScheduledHint': '{ahead} classes still to run — not yet delivered.',
    'company.enrollmentStates': 'Seats by state',
    'company.enrollmentStatesHint': 'Withdrawn seats are counted here and are not in "seats used" — a returned seat is why that number can go down.',
    'company.noEnrollmentsYet': 'Nobody has been enrolled yet.',
    'company.whatsNext': 'Where to look next',
    'company.goLearning': 'How the modules are going',
    'company.goReadiness': 'Who is ready',
    'company.goAttendance': 'Attendance — {count} class(es) still to come',

    'company.peopleLead': 'The people you can send on training. Naming somebody costs no seat; a seat is spent when you enrol them.',
    'company.peopleTotal': 'People named',
    'company.peopleTotalHint': 'On your roster, whether enrolled or not.',
    'company.peopleWithSignIn': 'Can sign in',
    'company.peopleWithoutSignIn': 'No sign-in yet',
    'company.peopleWithoutSignInHint': 'On your account but cannot see anything you bought.',
    'company.peopleEnrollable': 'Ready to enrol',
    'company.peopleEnrollableHint': 'Have a learner profile and can be put into a cohort.',
    'company.cohortsLead': 'The deliveries booked against your account. A cohort with nobody in it yet is still yours — fill it from Seats in use.',
    'company.cohortsTotal': 'Cohorts booked',
    'company.cohortsRunning': 'Running now',
    'company.cohortsOpenToEnrol': 'Still have room',
    'company.cohortsOpenToEnrolHint': 'Can still take more of your people.',
    'company.cohortsOurLearners': 'Your seats across cohorts',

    'company.programmes': 'Programmes',
    'company.programmesLead': 'What your agreed offer named. This is not our whole catalogue — it is what was sold to you.',
    'company.noProgrammes': 'No programme is matched to your account yet',
    'company.verifiedPath': 'Confirmed',
    'company.verified': 'Confirmed with you',
    'company.asRequested': 'As requested',
    'company.programmesToRequests': 'Ask us to run one of these',
    'company.programmesTotal': 'Programmes offered',
    'company.programmesConfirmedCount': 'Confirmed with you',
    'company.programmesAsRequestedCount': 'As requested',

    'company.enrollments': 'Seats in use',
    'company.enrollmentsLead': 'One row per seat you are spending, and what has come of it. Withdrawn rows stay here — they spend nothing, and they are why your usage can fall.',
    'company.noEnrollments': 'No seats in use yet',
    'company.noEnrollmentsHint': 'Enrol somebody from your people into one of your cohorts below.',
    'company.enrolledAt': 'Enrolled',
    'company.progress': 'Progress',
    'company.modulesOf': '{passed} of {total} modules passed',
    'company.lastPassed': 'Last pass',
    'company.enrollmentsTotal': 'Seats spent',
    'company.enrollmentsActive': 'Active',
    'company.enrollmentsCompleted': 'Completed',
    'company.enrollmentsWithdrawn': 'Withdrawn',
    'company.enrollmentsWithdrawnHint': 'Spend nothing — the reason usage can fall.',

    'company.learning': 'Learning',
    'company.learningLead': 'By module rather than by person: forty progress bars say nothing, and "eleven are stuck on the same module" says what to do.',
    'company.noLearning': 'Nothing to report yet',
    'company.noLearningHint': 'Once your people are enrolled and their modules are set up, this fills in on its own.',
    'company.module': 'Module',
    'company.onThisModule': 'People on it',
    'company.averageProgress': 'Average progress',
    'company.spread': 'Where they are',
    'company.spreadNotStarted': '{count} not started',
    'company.spreadInProgress': '{count} under way',
    'company.spreadPassed': '{count} passed',
    'company.spreadFailed': '{count} failed',
    'company.averageScore': 'Average score',
    'company.notGradedYet': 'Not graded yet',
    'company.notAssessed': 'Not assessed',
    'company.learningModulesTotal': 'Modules under way',
    'company.learningNotStartedTotal': 'Not started yet',
    'company.learningPassedTotal': 'Passed',
    'company.learningFailedTotal': 'Failed',

    'company.readiness': 'Readiness',
    // THREE CLAIMS, KEPT APART. Progress is content consumed, passed credit is
    // work that was graded, and the programme bar is the credential verdict. The
    // lead line exists so a reader does not take the first for the third.
    'company.readinessLead': 'Three different things, side by side: how much of the course they have been through, how much of it they have passed, and where that puts them.',
    'company.noReadiness': 'Nobody is enrolled yet',
    'company.noReadinessHint': 'Readiness appears once your people are on a cohort and their work starts being marked.',
    'company.passedCredit': 'Passed',
    'company.mandatory': 'Required modules',
    'company.mandatoryDone': 'All passed',
    'company.mandatoryOpen': 'Some left',
    'company.competencies': 'Skills qualified',
    'company.readinessTotal': 'People assessed',
    'company.readinessMandatoryDoneCount': 'All required passed',
    'company.readinessAvgPassedCredit': 'Average passed credit',
    'company.readinessMeetsBarCount': 'Meet the programme bar',
    'company.readinessMeetsBarHint': 'Ready for their programme credential.',
    'company.unmetModules': 'Modules outstanding',

    // ATTENDANCE IS AN ASSERTION, NOT A MEASUREMENT, and the lead line has to
    // say so: the reader is the employer of the people being counted, and a
    // number they think came from a computer is a number they will act on.
    'company.attendanceLead': 'Taken by the instructor in class, the way a register is. Nothing here is measured from anybody’s screen, and it can be corrected.',
    'company.noSessions': 'No classes scheduled',
    'company.noSessionsHint': 'Live classes appear here once your cohort has them on the calendar.',
    'company.sessions': 'Classes',
    'company.when': 'When',
    'company.session': 'Class',
    'company.ourAttendance': 'Your people',
    'company.notHeldYet': 'Not held yet',
    // Held, but nobody has said who was there. NOT the same as nobody coming.
    'company.registerNotTaken': 'No register taken',
    'company.presentOf': '{present} of {enrolled} there',
    'company.attendanceByPerson': 'By person',
    'company.sessionsExpected': 'Classes expected',
    'company.sessionsAttended': 'Attended',

    'person.phone': 'Phone',
    'program.code': 'Code',
    'common.copy': 'Copy',
    'common.copied': 'Copied',
    'common.done': 'Done',

    // ─── Placement (find this customer a cohort) ─────────────────────────────
    'placement.action': 'Place in cohort',
    'placement.title': 'Place {subject} in a cohort',
    'placement.confirmedProgramme': 'Confirmed programme',
    'placement.programmeUnknown': 'Not recognised',
    'placement.confirmedSeats': '{count} seats confirmed',
    'placement.inferred': 'Worked out from the modules requested — this programme covers {matched} of {requested}. Not covered: {uncovered}.',
    'placement.unrecognised': 'The offer asks for {codes}, which this catalogue does not sell. Check the offer in before placing anybody.',
    'placement.noOffer': 'No confirmed offer for this customer — they were not created from a won deal. Pick a cohort from the register instead.',
    'placement.who': 'Placing {count}',
    'placement.notEnrollable': 'No learner profile',
    'placement.candidates': 'Cohorts that fit',
    'placement.noCandidates': 'No cohort of this programme is open yet — start one below.',
    'placement.otherMarket': 'Other country',
    'placement.alreadyThere': 'Already enrolled',
    'placement.place': 'Place here',
    'placement.notEnoughSeats': 'Only {available} seats free, {needed} needed.',
    'placement.startNew': 'Start a new cohort',
    'placement.newCohort': 'New cohort for {programme}',
    'placement.newCohortHint': 'Programme, market and seats come from the confirmed deal; curriculum {version} is pinned at creation. Name it and date it.',
    'placement.createAndPlace': 'Create and place {count}',

    // ─── Cohort requests (the company asks, the Academy commits) ─────────────
    'cohortRequest.title': 'Cohort requests',
    'cohortRequest.lead': 'Tell us which programme you want run and roughly when. We book the instructor and start the cohort, then you fill its seats yourself.',
    'cohortRequest.ask': 'Request a cohort',
    'cohortRequest.programme': 'Programme',
    'cohortRequest.pickProgramme': 'Choose a programme…',
    'cohortRequest.seats': 'Seats to use',
    'cohortRequest.seatsHint': 'You have {count} left.',
    'cohortRequest.preferredStart': 'Preferred start',
    'cohortRequest.note': 'Anything we should know',
    'cohortRequest.send': 'Send request',
    'cohortRequest.none': 'No requests yet.',
    'cohortRequest.noProgrammes': 'Your offer does not name a programme we can match to our catalogue yet. Your Academy contact can confirm what was agreed.',
    'cohortRequest.noSeats': 'All your seats are spent. Talk to us about more before asking for another cohort.',
    'cohortRequest.waiting': 'With the Academy',
    'cohortRequest.fulfilled': 'Cohort started',
    'cohortRequest.declined': 'Declined',
    'cohortRequest.summary': '{seats} seats · preferred start {when}',
    'cohortRequest.noPreference': 'no preference',
    'cohortRequest.queueLead': 'Companies asking for a cohort to be run. Start one for them, or say why not.',
    'cohortRequest.openTab': 'Open',
    'cohortRequest.allTab': 'All',
    'cohortRequest.queueEmpty': 'No cohort requests',
    'cohortRequest.queueEmptyHint': 'When a company with seats asks for a cohort, it lands here.',
    'cohortRequest.asking': 'Wants {seats} seats · preferred start {when}',
    'cohortRequest.askedBy': 'Asked by {who} · {when}',
    'cohortRequest.fulfil': 'Start cohort',
    'cohortRequest.decline': 'Decline',
    'cohortRequest.fulfilTitle': 'Answer {company}',
    'cohortRequest.fulfilHint': 'Name the cohort you started for them. Not created yet?',
    'cohortRequest.createCohort': 'Create one',
    'cohortRequest.cohort': 'Cohort',
    'cohortRequest.pickCohort': 'Choose a cohort…',
    'cohortRequest.noteToCompany': 'Note to the company',
    'cohortRequest.declineTitle': 'Decline {company}',
    'cohortRequest.reason': 'Reason',
    'cohortRequest.reasonHint': 'The company is shown this.',

    // ─── Live toasts ─────────────────────────────────────────────────────────
    'toast.crmArrival': 'A new customer just arrived.',
    'toast.viewPeople': 'View people',

    'company.enrol': 'Enrol learners',
    'company.chooseCohort': 'Cohort',
    'company.chooseCohortPlaceholder': 'Choose one of your cohorts…',
    'company.noOpenCohorts': 'None of your cohorts has room right now',
    'company.addLearner': 'Add a learner',
    'company.noEnrollableLearners': 'Nobody left to add',
    'company.enrolSubmit': 'Enrol {count} learner(s)',

    // ── The staff side ──────────────────────────────
    'company.name': 'Company',
    'company.segment': 'Segment',
    // The kind, keyed by its wire value ("company" | "university").
    'company.kind': 'Kind',
    'company.kind.company': 'Company',
    'company.kind.university': 'University',
    'company.kindHint': 'Set by from what the customer told us; correct it here if was wrong.',
    'company.seats': 'Seats',
    'company.memberCount': 'Members',
    'company.members': 'Members of {name}',
    'company.status': 'Status',
    'company.open': 'Open',
    'company.closed': 'Closed',
    'company.statTotal': 'Companies',
    'company.statActive': 'Active',
    'company.statMembers': 'Members',
    'company.statNoSeatsBought': 'No seats bought',
    'company.edit': 'Edit company',
    'company.noneYet': 'No company accounts yet',
    'company.noneYetHint': 'Accounts arrive with won deals, together with the seats each one bought.',
    // Zero bought is a real state — a won deal whose offer named no seat count
    // — and it must not render as an empty bar, which reads as plenty of room.
    'company.noSeatsBought': 'No seats bought',
    'company.seatAllowance': 'Seats bought',
    'company.seatAllowanceFrozen': 'Set by the won deal in, and raised by the next one. Not editable here — the seats are what the customer bought.',
    'company.crmAccountId': 'account id',
    'company.crmAccountFrozen': 'Fixed after creation: it is the account’s identity, and repointing it would move the whole cohort list.',
    'company.active': 'Open for new enrollments',
    'company.activeHint': 'Closing keeps every record and stops new enrollments.',
    'company.noMembers': 'Nobody in this company yet',
    'company.noMembersHint': 'Names arrive with the won deal, or from the company’s own manager in their portal.',
    'company.rosterOwnerHint': 'The won deal names the people it covers, and the company’s manager adds the rest from their own portal. Staff hand out sign-ins here.',

    // The COMPANY's allowance, not a cohort's seats — the recoveries are
    // opposites, so the two must never share a sentence.
    'error.seat_allowance': 'That company has no seats left.',


    // ═══════════════════════════════════════════════════════════════════════
    // CURRICULUM SCOPING — everything below is about WHICH SUBJECT a thing
    // belongs to. See docs/MULTI-CURRICULUM.md.
    // ═══════════════════════════════════════════════════════════════════════

    // ─── Levels: the two writes a level did not have ─────────────────────────
    'curriculum.levelTitle': 'Title',
    'curriculum.editLevel': 'Edit level',
    'curriculum.editLevelHint': 'Every field here is editable: a level has no code, and every requirement in the system names a module.',
    'curriculum.deleteLevel': 'Delete level',
    'curriculum.deleteLevelConfirm': 'Delete {title}? Only an empty level can be removed.',
    'curriculum.deleteLevelWhy': 'Deleting a level takes its modules — and the lessons, quizzes and requirements reached through them — with it. So it is offered only while the level holds nothing.',
    'curriculum.levelNotEmpty': 'Holds {count} module(s) — remove them first to delete this level.',
    'curriculum.levelOrdinalTaken': 'Two levels cannot share an ordinal. Move the other one first.',

    // ─── The dimension vocabulary, now editable ──────────────────────────────
    'curriculum.createDimension': 'New dimension',
    'curriculum.editDimension': 'Edit dimension',
    'curriculum.deleteDimension': 'Delete dimension',
    'curriculum.deleteDimensionConfirm': 'Delete {name}? Only a dimension nothing references can be removed.',
    'curriculum.dimensionCode': 'Code',
    'curriculum.dimensionCodeHint': 'Short and uppercase, permanent from creation — module tags and the public pillars both name it.',
    'curriculum.dimensionCodeLocked': 'Set once and never changed. Module tags and dimension tools hold it as a foreign key, and the public page names it by code — so a rename would re-point some and orphan others without a word.',
    'curriculum.dimensionName': 'Name',
    'curriculum.dimensionContentCore': 'Content core',
    'curriculum.dimensionContentCoreHint': 'One line describing what this axis covers. It is what the public pillars section prints under the name.',
    'curriculum.dimensionOrdinal': 'Display order',
    'curriculum.dimensionTools': 'Canonical tools',
    'curriculum.dimensionToolsHint': 'What this axis claims to be taught on, as opposed to a module’s own tools, which is what one actually teaches on.',
    'curriculum.dimensionsShared': 'The vocabulary is academy-wide and does not narrow with the curriculum above: a dimension is named by code from module tags and the public page at once, so two subjects cannot each hold their own “3D”. Only the counts are per curriculum.',
    'curriculum.noDimensions': 'No dimensions',
    'curriculum.noDimensionsHint': 'Create the axes the curriculum tags its modules with.',
    'curriculum.dimensionInUse': 'Referenced by {count} module(s) — remove the tag from those first.',

    // ─── Which edition the public page shows ─────────────────────────────────
    'curriculum.publicVersion': 'Public page',
    'curriculum.publicVersionAuto': 'Automatic',
    'curriculum.publicVersionAutoHint': 'No edition is pinned, so the public page shows whichever is newest and in effect.',
    'curriculum.publicVersionPinnedHint': 'Pinned to {label}. The public page shows this edition rather than deriving one — while it is in effect and not retired.',
    'curriculum.setPublicVersion': 'Show this on the public page',
    'curriculum.clearPublicVersion': 'Back to automatic',
    'curriculum.publicVersionNotEffective': 'Pinned, but not in effect today — the public page is showing another edition until this one’s date arrives. A pin never publishes early.',
    'curriculum.pinnedBadge': 'Pinned',

    // ─── A programme belongs to a subject ────────────────────────────────────
    'program.curriculum': 'Curriculum',
    'program.curriculumHint': 'Which programme of study this is sold out of — pick it first, since the module bundle you set afterwards must come from this curriculum.',
    'program.curriculumLocked': 'Set once. Moving a programme to another subject would leave every module in its bundle behind — and the bundle is what a cohort enrolls against.',
    'program.pickCurriculumFirst': 'Pick a curriculum to create a programme.',
    'program.noCurricula': 'No curricula yet — create one on the Curriculum screen first. A programme is sold out of a subject, so there is nothing to file this one under.',
    'program.allCurricula': 'All curricula',
    'program.curriculumFilterHint': 'Programmes across every subject. Pick one to see only what it sells.',



    // ── The price book (§ plan-3 phase 4's last screen) ──────────────────────
    // Phase 4 moved the price list out of the browser into eight tables and
    // left them editable only by SQL. These are the words on the screen that
    // finally opened them.
    'nav.priceBook': 'Price book',
    'nav.priceBook.what': 'What a membership costs, and every discount code',
    'priceBook.lede': 'What the Academy charges for a membership, per market, and the codes that discount it. Changing a price moves what the next buyer is quoted; it never restates an order somebody has already paid.',
    'priceBook.tab.plans': 'Plans and prices',
    'priceBook.tab.promotions': 'Promotion codes',
    'priceBook.edit': 'Edit',
    'priceBook.showRetired': 'Include retired plans',
    'priceBook.onSale': 'On sale',
    'priceBook.retired': 'Retired',
    'priceBook.noDefaultPrice': 'Not priced — not on sale',
    'priceBook.retire': 'Retire',
    'priceBook.restore': 'Restore',
    'priceBook.accountKind.person': 'Individual buyer',
    'priceBook.accountKind.company': 'Organisation',

    'priceBook.plan.create': 'New plan',
    'priceBook.plan.edit': 'Edit plan',
    'priceBook.plan.createNote': 'A new plan is not on sale until it has a default currency and at least one programme priced. Deciding a plan exists and deciding what it costs are two separate acts.',
    'priceBook.plan.code': 'Code',
    'priceBook.plan.codeHint': 'The word the landing page, the arrivals screen and three locales will use for this plan. Lowercase.',
    'priceBook.plan.codeFixed': 'Fixed: this code is on every order bought under this plan.',
    'priceBook.plan.name': 'Name',
    'priceBook.plan.accountKind': 'Buyer',
    'priceBook.plan.accountKindHint': 'Which tab of the arrivals queue a buyer of this plan lands in. It cannot be changed afterwards.',
    'priceBook.plan.accountKindFixed': 'Fixed: changing it would move people who have already arrived.',
    'priceBook.plan.minSeats': 'Minimum seats',
    'priceBook.plan.maxSeats': 'Maximum seats',
    'priceBook.plan.displayOrder': 'Order',
    'priceBook.plan.displayOrderHint': 'Where it sits on the membership cards.',
    'priceBook.plan.seatBand': 'From {min} to {max} seats',
    'priceBook.plan.statTotal': 'Plans',
    'priceBook.plan.statOnSale': 'On sale',
    'priceBook.plan.statNoDefaultPrice': 'Not priced',
    'priceBook.plan.statMarketOverrides': 'Market overrides',
    'priceBook.plan.none': 'No plans in the book',
    'priceBook.plan.noneHint': 'Nothing is on sale until a plan exists and carries a default price.',

    'priceBook.price.add': 'Add a market',
    'priceBook.price.edit': 'Edit currency and tax',
    'priceBook.price.market': 'Market',
    'priceBook.price.marketHint': 'Leave empty for the default — the price every market without a local one pays.',
    'priceBook.price.default': 'Default',
    'priceBook.price.defaultOption': 'Default (every other market)',
    'priceBook.price.defaultNote': 'The default is a row, not a blank: the currency and tax every market without its own row uses. A plan without one is not on sale anywhere. What a seat costs is set per programme below.',
    'priceBook.price.currency': 'Currency',
    'priceBook.price.currencyHint': 'A new row is in the currency picked in the header. To price this market in another currency, switch it first.',
    'priceBook.price.currencyStoredHint': 'This row\'s currency is fixed once written; only the tax can change.',
    'publicPrice.approxTitle': 'Approximate, at today\'s indicative rate. You are charged {price}.',
    'publicPrice.approxLabel': 'Approximate prices',
    'priceBook.price.unitPrice': 'Per seat',
    'priceBook.price.unitPriceHint': 'What this programme adds to one seat, before any discount.',
    'priceBook.price.tax': 'Tax',
    'priceBook.price.taxPercent': 'Tax',
    'priceBook.price.taxPercentHint': 'Charged on the discounted total and shown to the buyer as its own line.',
    'priceBook.price.none': 'No currency set yet — this plan is not on sale.',
    'priceBook.price.remove': 'Remove this market’s currency and tax',
    'priceBook.programs.heading': 'Programme prices',
    'priceBook.programs.lede': 'A seat costs the sum of the programmes the buyer ticks. A programme with no price here is not sold under this plan.',
    'priceBook.programs.add': 'Price a programme',
    'priceBook.programs.edit': 'Edit programme price',
    'priceBook.programs.none': 'No programme is priced — this plan is not on sale.',
    'priceBook.programs.programme': 'Programme',
    'priceBook.programs.pick': 'Choose a programme',
    'priceBook.programs.notFiled': 'not filed for this audience',
    'priceBook.programs.remove': 'Remove this programme price',
    'priceBook.programs.marketHint': 'Leave empty for the default — what this programme costs in every market without its own figure.',
    'priceBook.programs.currencyNote': 'Stated in {currency}, this plan’s currency for that market.',
    'priceBook.programs.needsCurrency': 'Set this plan’s default currency and tax first — a programme price is stated in it.',
    'priceBook.breaks.button': 'Volume breaks ({count})',
    'priceBook.breaks.title': 'Volume breaks — {plan}',
    'priceBook.breaks.oneApplies': 'Only one break applies: the highest threshold the order reaches. They never stack.',
    'priceBook.breaks.minSeats': 'From seats',
    'priceBook.breaks.rate': 'Discount',
    'priceBook.breaks.add': 'Add a break',
    'priceBook.breaks.remove': 'Remove this break',
    'priceBook.breaks.none': 'No volume breaks. A plan sold one seat at a time has no volume to break on.',
    'priceBook.breaks.summary': 'Volume:',
    'priceBook.breaks.item': '{seats}+ seats, {rate} off',

    'priceBook.promo.create': 'Issue a code',
    'priceBook.promo.edit': 'Edit code',
    'priceBook.promo.scopeNote': 'Scope is by absence: no plan means every plan, no market means every market. Leave both for a launch code; narrow one for a partnership.',
    'priceBook.promo.code': 'Code',
    'priceBook.promo.codeHint': 'Uppercased when saved, so one spelling is one code.',
    'priceBook.promo.codeFixed': 'Fixed: this code is named on every order quoted under it.',
    'priceBook.promo.rate': 'Discount',
    'priceBook.promo.rateHint': 'Off the subtotal. Never compounded with a volume break — both come off the same figure.',
    'priceBook.promo.from': 'From',
    'priceBook.promo.to': 'Last day',
    'priceBook.promo.windowHint': 'Empty means since always.',
    'priceBook.promo.toHint': 'The last day it works. Empty means until further notice.',
    'priceBook.promo.plan': 'Plan',
    'priceBook.promo.planHint': 'Which membership it applies to.',
    'priceBook.promo.everyPlan': 'Every plan',
    'priceBook.promo.market': 'Market',
    'priceBook.promo.marketHint': 'A market-scoped code is refused on a quote that has not named a country.',
    'priceBook.promo.everyMarket': 'Every market',
    'priceBook.promo.maxRedemptions': 'Redemption ceiling',
    'priceBook.promo.maxRedemptionsHint': 'Empty is unlimited. Counted in confirmed payments, not abandoned checkouts.',
    'priceBook.promo.unlimited': 'Unlimited',
    'priceBook.promo.spent': 'Already redeemed {count} times — a ceiling below that is refused.',
    'priceBook.promo.statTotal': 'Codes',
    'priceBook.promo.statLive': 'Live',
    'priceBook.promo.statNotPricing': 'Not currently pricing',
    'priceBook.promo.statRedemptions': 'Redemptions',
    'priceBook.promo.activeOnly': 'Live codes only',
    'priceBook.promo.live': 'Live',
    'priceBook.promo.notPricing': 'Not pricing',
    'priceBook.promo.withdrawn': 'Withdrawn',
    'priceBook.promo.withdraw': 'Withdraw',
    'priceBook.promo.reinstate': 'Reinstate',
    'priceBook.promo.scope': 'Applies to',
    'priceBook.promo.window': 'Window',
    'priceBook.promo.alwaysOn': 'No window',
    'priceBook.promo.redeemed': 'Redeemed',
    'priceBook.promo.ofUnlimited': '{count} of unlimited',
    'priceBook.promo.none': 'No promotion codes',
    'priceBook.promo.noneHint': 'A promotion is a commercial decision with an issuer, a window and a ceiling. None have been made.',

    // ─── What needs you (§6b.2) ─────────────────────────────────────────────
    // One label and one line per queue in config/needsYou.js. The second line
    // matters as much as the first: a count with no sentence under it is a
    // number somebody has to open the screen to understand.
    'needsYou.grading': 'Assessments to grade',
    'needsYou.grading.what': 'Submitted work waiting on a marker',
    'needsYou.quizMarking': 'Quiz answers to mark',
    'needsYou.quizMarking.what': 'Free-text answers submitted and waiting on a marker',
    'needsYou.completion': 'Credentials to issue',
    'needsYou.completion.what': 'People who finished and are not closed out',
    'needsYou.unstaffed': 'Cohorts with no instructor',
    'needsYou.unstaffed.what': 'Past Draft and nobody assigned to teach them',
    'needsYou.requests': 'Cohort requests',
    'needsYou.requests.what': 'Companies asking for a cohort we have not started',
    'needsYou.arrivalsCrm': 'Arrivals to admit',
    'needsYou.arrivalsCrm.what': 'Won deals waiting on verification and a sign-in',
    'needsYou.arrivalsHrm': 'HRM arrivals to admit',
    'needsYou.arrivalsHrm.what': 'Employees assigned to train or teach, waiting on a sign-in',
    'needsYou.arrivalsAcademy': 'Membership arrivals to admit',
    'needsYou.arrivalsAcademy.what': 'Memberships bought on the public page, waiting on verification',
    // ─── Screen intros (§6d.3) ────────────────────────────────────────────
    'screen.cohortCreate.intro': 'Choose the curriculum version here — creating the cohort pins and freezes it, so pick carefully before saving.',
    'screen.cohortDetail.intro': 'One record for this cohort\'s schedule, roster and engagement — add a session or enrol someone right from here.',
    'screen.enrollBatch.intro': 'Enrol many people into this cohort at once — choose atomic or best-effort before you paste the list.',
    'screen.completionBatch.intro': 'Paste completion rows from an LMS export to record progress for many enrolments in one pass.',
    'screen.grading.intro': 'Every submission waiting on a grader — confirm a score or send work back without opening each one.',
    'screen.enrollmentsQueue.intro': 'The people who did the work and are waiting on a coordinator — the dashboard’s second queue, as a list you can act from.',
    'screen.assessmentDetail.intro': 'One submission, whole — read the work, run the checks, then confirm a score or return it for another try.',
    'screen.credentials.intro': 'Look up what someone holds, or issue a credential — choose the holder first; credentials are never listed in bulk.',
    'screen.credentialDetail.intro': 'One credential as it was issued — renew it, revoke it, or open the same link an employer would use to verify it.',
    'screen.curriculum.intro': 'Every curriculum version, pinned or retired — open one to edit its levels and modules before it freezes.',
    'screen.curriculumVersionDetail.intro': 'This version\'s structure locks the moment a cohort pins it — edit its levels and modules while it\'s still open.',
    'screen.curriculumModuleDetail.intro': 'One module\'s permanent code, its editable prose, and the dimension and tool sets it teaches.',
    'screen.quizEditor.intro': 'Build this quiz\'s questions and options here — reorder them, then publish when the module is ready to test.',
    'screen.programs.intro': 'What the Academy sells, by curriculum — open one to see its modules and the scoring its credential asks for.',
    'screen.programDetail.intro': 'This program\'s modules and standards, and the module scoring its credential is issued against.',
    // THE TESTS HR SENDS (STANDALONE-ASSESSMENTS-PLAN §1).
    'nav.assessments': 'Assessments',
    'nav.assessments.what': 'The tests HR sends',
    'screen.assessments.intro': 'The named tests HR sends to applicants and employees — each one a set of published screening quizzes and tasks, labelled with the skills it measures.',
    'screen.assessmentEditor.intro': 'What this test says it measures, and which screening items it is made of — publish it to offer it to HR.',
    'assessment.create': 'New assessment',
    'assessment.name': 'Name',
    'assessment.namePlaceholder': 'Revit Fundamentals',
    'assessment.description': 'Description',
    'assessment.skills': 'Skills measured',
    'assessment.skillsHint': 'The words HR reads beside an opening’s required skills — nothing matches them automatically.',
    'assessment.skillAdd': 'Add',
    'assessment.skillPlaceholder': 'Revit',
    'assessment.skillRemove': 'Remove {skill}',
    'assessment.skillDuplicate': 'That skill is already listed.',
    'assessment.language': 'Language',
    'assessment.languageHint': 'Every item on the test must be written in it.',
    'assessment.passingScore': 'Passing score',
    'assessment.passingScoreHint': 'Shown to HR beside the result. It decides nothing here.',
    'assessment.timeLimit': 'Time limit (minutes)',
    'assessment.timeLimitHint': 'Leave blank for the items’ own total.',
    'assessment.items': 'What is on the test',
    'assessment.itemsHint': 'Published screening quizzes and tasks, in the order the candidate meets them. Order and removals save as you make them.',
    'assessment.itemsEmpty': 'Nothing on this test yet.',
    'assessment.addItems': 'Add items',
    'assessment.addChosen': 'Add {n}',
    'assessment.itemSearchPlaceholder': 'Search by title, module or skill code…',
    'assessment.itemQuizMeta': '{questions} questions · {minutes} min',
    'assessment.itemTaskMeta': 'Practical · {minutes} min',
    'assessment.itemUnpublished': 'Unpublished since',
    'assessment.alreadyAdded': 'Already added',
    'assessment.roomLeft': 'Room for {n} more',
    'assessment.noItems': 'No published screening items in this language',
    'assessment.noItemsHint': 'Screening quizzes and tasks are authored on the module that teaches the skill, then published there.',
    'assessment.searchPlaceholder': 'Search by name or skill…',
    'assessment.empty': 'No assessments yet',
    'assessment.emptyHint': 'An assessment is a named test HR can send — build one from published screening quizzes and tasks.',
    'assessment.noMatches': 'No assessment matches',
    'assessment.noMatchesHint': 'The search looks at names and skill words.',
    'assessment.noSkills': 'No skills named yet',
    'assessment.summary': '{items} items · {questions} questions · {minutes} min',
    'assessment.sentCount': 'Sent {n} times',
    'assessment.draft': 'Draft',
    'assessment.published': 'Published',
    'assessment.archived': 'Archived',
    'assessment.publish': 'Publish',
    'assessment.unpublish': 'Unpublish',
    'assessment.archive': 'Archive',
    'assessment.restore': 'Restore',
    'assessment.duplicate': 'Duplicate',
    'assessment.backToList': 'All assessments',
    'assessment.showArchived': 'Show archived',
    'assessment.standalone': 'Standalone',
    'screen.checkCodes.intro': 'The closed vocabulary of findings graders can raise — an empty list means instructors grade unaided.',
    'screen.priceBook.intro': 'What a seat costs and any promotions running against it — two tabs, because a price and a discount differ.',
    'screen.enrollmentDetail.intro': 'One learner\'s progress in one cohort — record completions, waive a module, or move them through their journey.',
    'screen.instructorAvailability.intro': 'Who is away and when — an empty table with no watermark means nobody told us yet, not that nobody is away.',
    'screen.people.intro': 'Every person the Academy knows — learners, instructors and staff — search here before opening one record.',
    'screen.personDetail.intro': 'One person\'s profile and credentials — edit their record, place them in a cohort, or rate their standing.',
    'screen.companies.intro': 'The B2B accounts and the seats they bought — open one to manage its members or place people into a cohort.',
    'screen.cohortRequests.intro': 'Companies asking for a cohort that doesn\'t exist yet — answer by naming the cohort you started, or say no with a reason.',
    'screen.learnModule.intro': 'This module as a learner sees it — lessons, the assignment and any quiz, with your progress ticked off as you go.',
    'screen.lessonPlayer.intro': 'One lesson, played — watch or read the material, take notes, and mark it done when you\'re through.',
    'screen.my.intro': 'Your own enrolments and credentials — nothing here is a staff screen, just the records that are yours.',
    'screen.arrivals.intro': 'Newly arrived customers, waiting on verification — call to confirm them, then issue their sign-in.',
    'screen.arrivalsHrm.intro': 'Employees the HRM assigned to train or teach — verify them, then issue the sign-in that gets them started.',
    'screen.arrivalsAcademy.intro': 'Memberships bought on the public page — admit anyone whose account failed to create, then issue their sign-in.',
    'screen.company.intro': 'Your account at a glance — seats, cohorts and what your people are learning, all from your own dashboard.',
    'screen.companyProgrammes.intro': 'What your account was actually sold — not the public catalogue, just the paths available to you.',
    'screen.companyCohorts.intro': 'The cohorts run for your account, found by your company rather than who\'s enrolled — even an empty one stays listed.',
    'screen.companyPeople.intro': 'Everyone your account can send to training — add someone new or issue their sign-in from here.',
    'screen.companyEnrollments.intro': 'Enrol your people into a cohort you already hold a seat in — this spends a seat immediately.',
    'screen.companyRequests.intro': 'Ask for a cohort that doesn\'t exist yet — different from enrolling, since nothing commits until the Academy answers.',
    'screen.companyLearning.intro': 'What your people are learning, one row per module rather than per person.',
    'screen.companyReadiness.intro': 'The competency engine\'s verdict for every seat your account holds.',
    'screen.companyAttendance.intro': 'Who showed up to which session, across every cohort your account is running.',

    // ─── Marking context, feedback and the learner's mark ──────────────────────
    'grading.feedbackToLearner': 'Comments for the learner',
    'grading.feedbackToLearnerHint': 'Optional. Shown beside the mark and emailed to the learner with it — say what was good and what to do next.',
    'grading.waitingSince': 'handed in {when}',
    'grading.handedInUnrecordedShort': 'hand-in not recorded',
    'grading.dueOn': 'due {date}',
    'learn.dueOn': 'Due {date}. Your instructor set this date for your cohort.',
    'learn.markedScore': 'Marked: {score}%',
    'learn.markerComments': 'Your instructor\'s comments',
    'learn.noMarkerComments': 'Your instructor did not add comments.',

    // ─── Messages to learners and due dates ────────────────────────────────────
    'message.open': 'Message learners',
    'message.title': 'Write to learners',
    'message.toCohort': 'Everyone currently in cohort {cohort} will get this by email.',
    'message.toSome': 'Sent by email to: {names}',
    'message.subject': 'Subject',
    'message.body': 'Message',
    'message.bodyHint': 'Sent from the Academy with your name and email address, so learners can write back to you.',
    'message.send': 'Send',
    'message.sent': 'Sent to {count} learner(s).',
    'deadline.label': 'Work due on',
    'deadline.save': 'Set due date',
    'deadline.clear': 'Remove',
    'deadline.hint': 'Learners see this date, and anyone who has not handed in is reminded two days before. Late work is still accepted.',
    'deadline.dueOn': 'Work for this module is due {date}.',

    // ─── The learner's home, course page and schedule ──────────────────────────
    'nav.myRecords': 'Records & certificates',
    'nav.myRecords.what': 'Every course and certificate',
    'nav.schedule': 'My schedule',
    'nav.schedule.what': 'Classes and due dates',
    'screen.myHome.intro': 'Your next lesson, work that is due or came back, and your classes for the next two weeks — in one place.',
    'screen.schedule.intro': 'Every class and due date you are part of, as a learner or as an instructor. Add them to your own calendar in one click.',
    'myHome.title': 'My learning',
    'myHome.greeting': 'Welcome back, {name}',
    'myHome.subtitle': 'Here is what to do next.',
    'myHome.continue': 'Continue learning',
    'myHome.instructor': 'Instructor: {name}',
    'myHome.modulesDone': '{done} of {total} modules done',
    'myHome.nextLesson': 'Next: {module} — {lesson}',
    'myHome.nextModule': 'Next: {module}',
    'myHome.allCaughtUp': 'You are up to date. Waiting on your instructor for the next step.',
    'myHome.viewCourse': 'View course',
    'myHome.askInstructor': 'Ask my instructor',
    'myHome.due': 'Due and sent back',
    'myHome.nothingDue': 'Nothing is due in the next month, and nothing has been sent back.',
    'myHome.sentBack': 'Sent back',
    'myHome.overdue': 'Was due {date}',
    'myHome.dueOn': 'Due {date}',
    'myHome.classes': 'Upcoming classes',
    'myHome.noClasses': 'No live classes in the next two weeks.',
    'myHome.openClass': 'Open',
    'myHome.openWork': 'Open the work',
    'myHome.marks': 'Marks that came back',
    'myHome.readComments': 'Read the comments',
    'myHome.certificates': 'Certificates',
    'myHome.certificateOnItsWay': 'You finished {program}. Your certificate is being prepared — we will email you the moment it is issued.',
    'myHome.certificatesHeld': 'You hold {count} certificate(s) — view, share or print them',
    'myHome.whatNext': 'What next',
    'myHome.explore': 'Explore more programmes',
    'myHome.exploreHint': 'See what else the Academy teaches, and what it leads to.',
    'myHome.records': 'My records and certificates',
    'myHome.recordsHint': 'Every course you have taken, and every certificate you hold.',
    'myHome.calendarName': 'Binaa Academy',
    'myCourse.title': 'My course',
    'myCourse.runs': 'Runs {from} – {to}',
    'myCourse.modules': 'Modules',
    'myCourse.standing.notStarted': 'Not started',
    'myCourse.standing.inProgress': 'In progress',
    'myCourse.standing.done': 'Done',
    'myCourse.standing.passed': 'Passed',
    'myCourse.standing.failed': 'Needs another try',
    'myCourse.standing.waived': 'Excused',
    'askInstructor.title': 'Write to {name}',
    'askInstructor.intro': 'About {program}. Your instructor gets this by email with your address, so they can reply to you directly.',
    'askInstructor.sent': 'Sent. Your instructor will reply to your email address.',
    'credentialShare.copyLink': 'Copy link',
    'credentialShare.linkedIn': 'Add to LinkedIn',
    'credentialShare.print': 'Print or save as PDF',
    'schedule.subtitle': 'Four weeks at a time, as a learner and as an instructor.',
    'schedule.addToCalendar': 'Add to calendar',
    'schedule.earlier': 'Earlier',
    'schedule.later': 'Later',
    'schedule.today': 'Today',
    'schedule.range': '{from} – {to}',
    'schedule.emptyTitle': 'Nothing scheduled in these four weeks',
    'schedule.emptyHint': 'Classes and due dates appear here as your instructors set them.',
    'schedule.minutes': '{count} min',
    'schedule.dueEvent': 'Due: {module} ({cohort})',
    'schedule.role.learner': 'Learning',
    'schedule.role.instructor': 'Teaching',
    'schedule.state.Held': 'Held',
    'schedule.state.Cancelled': 'Cancelled',
    'schedule.state.Scheduled': 'Scheduled',

    // ─── The instructor's own record, registers and marking ────────────────────
    'nav.myTeaching': 'My teaching',
    'nav.myTeaching.what': 'Hours, leave, feedback',
    'screen.myTeaching.intro': 'Your own record as the Academy holds it: hours, leave, registers still to check, and what your learners said.',
    'myTeaching.subtitle': 'What the Academy records about your teaching.',
    'myTeaching.notInstructor': 'You do not have an instructor profile',
    'myTeaching.notInstructorHint': 'This page is for people who teach. A coordinator can add an instructor profile to your record.',
    'myTeaching.delisted': 'You were removed from teaching new cohorts on {date}. Speak to your coordinator about re-certification.',
    'myTeaching.hours30': 'Hours, last 30 days',
    'myTeaching.hoursYear': 'Hours this year',
    'myTeaching.hoursHint': 'From classes marked held — the figure sent to HR.',
    'myTeaching.sessionsYear': '{count} classes held',
    'myTeaching.cohorts': 'Cohorts I teach',
    'myTeaching.noCohorts': 'You are not assigned to a running cohort right now.',
    'myTeaching.learners': '{count} learners',
    'myTeaching.toMark': '{count} to mark',
    'myTeaching.registersIntro': 'Holding a class marks everyone present. Open each class and untick anyone who was not there.',
    'myTeaching.toCloseOut': 'Classes to close out',
    'myTeaching.toCloseOutIntro': 'These classes are over but not marked held, so the lessons after them stay locked until you open each one and say whether it finished.',
    'myTeaching.awaitingNewDate': 'Needs a new date',
    'myTeaching.leave': 'My leave',
    'myTeaching.noLeave': 'No approved leave recorded from today on.',
    'myTeaching.leaveRange': '{from} – {to}',
    'myTeaching.leaveHint': 'Leave comes from HR once it is approved. If something is missing, ask HR — the Academy cannot change it.',
    'myTeaching.comments': 'What learners said',
    'myTeaching.noComments': 'No written comments yet.',
    'myTeaching.standing': 'My standing',
    'myTeaching.certifiedOn': 'Certified on {date}',
    'myTeaching.lastAudit': 'Last review on {date}: {outcome}',
    'myTeaching.noAudit': 'Your teaching has not been reviewed yet.',
    'dashboard.registersToCheck': 'Registers to check',
    'dashboard.registersToCheckHint': 'Held classes still showing everyone present',
    'dashboard.attendanceUnchecked': '{count} register(s) not checked yet — counted as everyone present',
    'cohortSessions.registerChecked': 'Checked {when}',
    'cohortSessions.registerUnchecked': 'Not checked yet — everyone is marked present until you save this register.',

    'needsYou.registers': 'Registers to check',
    'needsYou.registers.what': 'Held classes still showing everyone present',
    'needsYou.closeOut': 'Classes to close out',
    'needsYou.closeOut.what': 'Classes that are over but not marked held',

    // ─── Company portal: seats, people, reports, billing ───────────────────────
    'export.csv': 'Export to spreadsheet',
    'export.completedAt': 'Completed',
    'export.modulesPassed': 'Modules passed',
    'export.modulesTotal': 'Modules',
    'export.moduleCode': 'Module code',
    'export.module': 'Module',
    'export.credits': 'Credits',
    'export.learners': 'Learners',
    'export.sessionsAttended': 'Classes attended',
    'companyImport.open': 'Import a list',
    'companyImport.lead': 'Paste from a spreadsheet or choose a CSV file. Columns: name, email, phone (optional). A heading row is fine.',
    'companyImport.pickFile': 'Choose a CSV file',
    'companyImport.paste': 'People to add',
    'companyImport.placeholder': 'Layla Hassan, layla@company.com, +201000000000',
    'companyImport.preview': '{count} people ready to add.',
    'companyImport.submit': 'Add {count} people',
    'companyImport.result': '{added} of {total} added. Anyone not added is listed below with the reason.',
    'companyImport.row': 'Row {row}',
    'companyMember.editTitle': 'Correct this person\'s details',
    'companyMember.nameHint': 'In English letters, exactly as it should appear on their certificate.',
    'companyMember.emailLocked': 'They already sign in with this address. Ask your Academy contact to change it.',
    'companyMember.remove': 'Remove',
    'companyMember.removeTitle': 'Remove {name} from your account?',
    'companyMember.removeMessage': 'They leave your list. Their own record and certificates stay theirs. Anyone still in a cohort must be withdrawn first.',
    'company.notEnrollableWhy': 'Not set up as a learner yet — ask your Academy contact.',
    'company.allOrNothing': 'Only enrol them if everyone gets a seat',
    'company.goReport': 'Management report',
    'company.targetRole': 'Preparing for',
    'company.browseCatalogue': 'See everything the Academy teaches',
    'company.askForQuote': 'Ask for more seats or a quote',
    'companySeat.swap': 'Give to someone else',
    'companySeat.withdraw': 'Withdraw',
    'companySeat.withdrawTitle': 'Withdraw {name} from {cohort}?',
    'companySeat.withdrawMessage': 'The seat comes back to your account. Their work so far stays on their record.',
    'companySeat.swapTitle': 'Give {name}\'s place in {cohort} to someone else',
    'companySeat.swapLead': 'We check the new person can take the place before anyone is withdrawn.',
    'companySeat.replacement': 'New person',
    'companySeat.swapConfirm': 'Hand over the place',
    'companyPerson.noSignIn': 'Cannot sign in yet',
    'companyPerson.credits': '{credits} credits earned',
    'companyPerson.noCourses': 'Not enrolled in any course yet.',
    'companyPerson.attendance': 'Attended {attended} of {expected} classes',
    'companyPerson.moduleCredits': '{credits} cr',
    'companyPerson.signedOff': 'Signed off by {name} on {when}',
    'companyPerson.signOff': 'Sign off this course',
    'companyPerson.supervisorSignOff': 'Supervisor sign-off',
    'companyPerson.signOffLead': 'Your organisation accepts this completed course. It is recorded with your name and does not change the Academy\'s own record.',
    'companyPerson.signOffNote': 'Note (optional), e.g. credit awarded',
    'companyPerson.signOffConfirm': 'Sign off',
    'nav.companyCertificates': 'Certificates',
    'nav.companyCertificates.what': 'Who holds what, until when',
    'nav.companyReport': 'Report',
    'nav.companyReport.what': 'One page for your management',
    'nav.companyBilling': 'Billing',
    'nav.companyBilling.what': 'Orders, receipts, renewals',
    'screen.companyCertificates.intro': 'Every certificate your people hold, each with its public page an auditor can check.',
    'screen.companyReport.intro': 'Seats, hours, outcomes and who needs attention — print it or save it as a PDF.',
    'screen.companyBilling.intro': 'What you bought through the Academy, with receipts, and when each membership renews.',
    'companyCertificates.lead': 'Certificates your people hold. Share the public link with anyone who needs proof.',
    'companyCertificates.title': 'Certificate',
    'companyCertificates.link': 'Public link',
    'companyCertificates.none': 'No certificates yet',
    'companyCertificates.noneHint': 'They appear here as your people finish their programmes.',
    'companyCertificates.noExpiry': 'Does not expire',
    'companyBilling.lead': 'Orders paid through the Academy\'s checkout, with receipts. Seats agreed in a sales contract are on the overview.',
    'companyBilling.memberships': 'Memberships and renewal dates',
    'companyBilling.noMemberships': 'No membership bought through the checkout.',
    'companyBilling.ref': 'Reference',
    'companyBilling.plan': 'Plan',
    'companyBilling.seats': 'Seats',
    'companyBilling.started': 'Started',
    'companyBilling.renews': 'Renews',
    'companyBilling.orders': 'Orders',
    'companyBilling.paid': 'Paid',
    'companyBilling.net': 'Before tax',
    'companyBilling.tax': 'Tax',
    'companyBilling.total': 'Total',
    'companyBilling.currency': 'Currency',
    'companyBilling.noOrders': 'No orders yet',
    'companyBilling.noOrdersHint': 'Seats agreed with our sales team are invoiced by them, not here.',
    'companyBilling.receipt': 'Receipt',
    'companyBilling.receiptTitle': 'Payment receipt',
    'companyBilling.billedTo': 'Billed to',
    'companyBilling.discount': 'Discount',
    'companyBilling.taxAt': 'Tax ({rate}%)',
    'companyBilling.receiptNote': 'A record of a payment received. For a tax invoice in your organisation\'s name, ask your Academy contact and quote the reference.',
    'companyRisk.title': 'Needs a look',
    'companyRisk.reasons': 'Why',
    'companyRisk.lastActive': 'Last active',
    'companyRisk.none': 'Nobody needs a look right now: everyone has started, is active, turning up and on time.',
    'companyRisk.lastActiveOn': 'Last active {date}',
    'companyRisk.neverActive': 'No activity yet',
    'companyRisk.attendance': 'attendance {rate}',
    'companyRisk.reason.notStarted': 'Not started',
    'companyRisk.reason.inactive': 'Quiet for 2+ weeks',
    'companyRisk.reason.lowAttendance': 'Low attendance',
    'companyRisk.reason.overdue': 'Work overdue',
    'companyRisk.reason.overdueCount': '{count} overdue',
    'companyRisk.reason.failed': 'Failed a module',
    'companyReport.lead': 'Everything below prints on a page or two. Use your browser\'s print dialog to save it as a PDF.',
    'companyReport.heading': 'Training report — {name}',
    'companyReport.asOf': 'As of {date}',
    'companyReport.investment': 'Seats and people',
    'companyReport.outcomes': 'Outcomes',
    'companyReport.delivery': 'Teaching received',
    'companyReport.hoursDelivered': 'Hours taught',
    'companyReport.hoursAttended': 'Hours attended by your people',
    'companyReport.hoursScheduled': 'Hours still scheduled',
    'companyReport.sessionsHeld': 'Classes held',
    'companyReport.passedOf': '{passed} of {learners} passed',
    'cohortRequest.preferredEnd': 'Start no later than',
    'cohortRequest.preferredEndHint': 'Optional — gives us a window instead of one date.',
    'cohortRequest.seating': 'How it is taught',
    'cohortRequest.seating.remote': 'Online',
    'cohortRequest.seating.academyPremises': 'At the Academy',
    'cohortRequest.seating.clientPremises': 'At our premises',
    'cohortRequest.language': 'Language',
    'cohortRequest.intake': 'Team or project',
    'cohortRequest.intakeHint': 'Optional — your own name for this group.',
    'cohortRequest.intakeUniversity': 'Intake or semester',
    'cohortRequest.intakeUniversityHint': 'E.g. "Spring 2027" or "Year 3 Civil".',
    'cohortRequest.withdrawn': 'Withdrawn',
    'cohortRequest.startBy': 'start by {date}',
    'cohortRequest.withdraw': 'Withdraw request',

    // ─── /about from the database: the public showcases and the site copy ───
    'nav.siteContent': 'Public site copy',
    'nav.siteContent.what': 'The words on the public About page, in every language',
    'screen.siteContent.intro': 'Override the About page’s hero, outcomes and resources; every save is audited.',
    'publicSite.published': 'Published',
    'publicSite.draft': 'Draft',
    'publicSite.notSetUp': 'Not on the public site',
    'publicSite.publish': 'Show on the public site',
    'publicSite.publishHint': 'Only with consent: publishing is refused until the date consent was given is recorded below.',
    'publicSite.consentAt': 'Consent recorded on',
    'publicSite.consentHint': 'The date this person or organisation agreed to be shown publicly. Required to publish.',
    'publicSite.slug': 'Public address',
    'publicSite.slugHint': 'Lowercase letters, digits and hyphens, e.g. layla-haddad.',
    'publicSite.sortOrder': 'Display order',
    'publicSite.tags': 'Tags',
    'publicSite.tagsHint': 'Comma-separated, at most 8.',
    'publicSite.photo': 'Photograph',
    'publicSite.logo': 'Logo',
    'publicSite.imageHint': 'JPEG, PNG or WebP, up to 5 MB.',
    'publicSite.removeImage': 'Remove image',
    'publicSite.chooseImage': 'Choose an image',
    'publicSite.dropImage': 'or drop it here',
    'publicSite.imagePending': 'New image: {name}',
    'publicSite.imageRemoved': 'The image will be removed when you save.',
    'publicSite.allLanguages': 'Write it in all three languages to publish; none stands in for another.',
    'publicProfile.title': 'Public profile',
    'publicProfile.edit': 'Edit public profile',
    'publicProfile.jobTitle': 'Title',
    'publicProfile.bio': 'Biography',
    'publicProfile.none': 'Not shown on the public About page.',
    'publicProfile.shownAs': 'Public address: {slug}',
    'showcase.action': 'Showcase',
    'showcase.edit': 'Public showcase — {name}',
    'showcase.group': 'Shown under',
    'showcase.group.corporate': 'Corporate clients',
    'showcase.group.academic': 'Academic partners',
    'showcase.displayName': 'Public name',
    'showcase.displayNameHint': 'The name the customer agreed to be shown under.',
    'showcase.descriptor': 'Descriptor',
    'showcase.summary': 'What was delivered',
    'showcase.facts': 'Facts',
    'showcase.factsHint': 'Up to four figures the customer agreed to, e.g. 240 / Staff trained.',
    'showcase.factValue': 'Value',
    'showcase.factLabel': 'Label',
    'showcase.addFact': 'Add a figure',
    'siteContent.block.about.hero': 'About — hero',
    'siteContent.block.about.outcomes': 'About — outcomes',
    'siteContent.block.about.resources': 'About — resources',
    'siteContent.unwritten': 'Not written yet — the About page shows nothing for this block',
    'siteContent.editedAt': 'Last saved {date}',
    'siteContent.reload': 'Load the latest version',
    'siteContent.saved': 'Saved',
    'siteContent.addRow': 'Add',
    'siteContent.remove': 'Remove',
    'siteContent.moveUp': 'Move up',
    'siteContent.moveDown': 'Move down',
    'siteContent.field.title': 'Title',
    'siteContent.field.lead': 'Lead',
    'siteContent.field.heading': 'Heading',
    'siteContent.field.note': 'Note',
    'siteContent.field.body': 'Text',
    'siteContent.field.icon': 'Icon',
    'siteContent.field.tag': 'Tag',
    'siteContent.field.items': 'Items',
    'siteContent.field.groups': 'Groups',
    // ─── The availability poll and generated sessions (SCHEDULING-PLAN A1, A2) ───
    'availability.cardTitle': 'Help choose your class times',
    'availability.cardIntro': 'Mark when you can attend. Your instructor picks the class times from everyone\'s answers.',
    'availability.deadline': 'Answer by {when}',
    'availability.zone': 'Times are in your own time zone: {zone}',
    'availability.answered': 'Answered',
    'availability.notAnswered': 'Not answered yet',
    'availability.mark': 'Mark my times',
    'availability.change': 'Change my answers',
    'availability.save': 'Save my answers',
    'availability.saved': 'Saved. You can change your answers until the deadline.',
    'availability.gridLabel': 'Times you can attend',
    'scheduling.title': 'Plan the class times',
    'scheduling.intro': 'Ask the learners when they are free, then generate this cohort\'s sessions from their answers.',
    'scheduling.ask': 'Ask learners when they are free',
    'scheduling.generate': 'Generate sessions',
    'scheduling.lessonsWaiting': '{count} live lessons have no session yet.',
    'scheduling.allScheduled': 'Every live lesson already has a session.',
    'scheduling.lastPattern': 'Last generated: {rule} at {time}.',
    'scheduling.pollHeading': 'Learners\' availability',
    'scheduling.state.open': 'Open',
    'scheduling.state.closed': 'Closed',
    'scheduling.state.expired': 'Expired',
    'scheduling.state.cancelled': 'Cancelled',
    'scheduling.progress': '{answered} of {total} answered',
    'scheduling.pollDeadline': 'Answers close {when}',
    'scheduling.heatmapLabel': 'How many learners can attend each time',
    'scheduling.zone': 'Times are in the cohort\'s time zone: {zone}.',
    'scheduling.busyLegend': 'Greyed out: you are usually busy then (other classes or leave).',
    'scheduling.nobody': 'nobody yet',
    'scheduling.ifNeededBy': 'if needed: {names}',
    'scheduling.busyWeeks': 'You are busy at this time in {busy} of {weeks} weeks.',
    'scheduling.suggestion': 'Best so far: {days} at {time}.',
    'scheduling.unanswered': 'Not answered yet: {names}',
    'scheduling.remind': 'Remind the rest',
    'scheduling.reminded': 'Reminder sent to {count}.',
    'scheduling.remindedNone': 'Nobody to remind just now (everyone was mailed in the last day).',
    'scheduling.cancelPoll': 'Cancel the poll',
    'scheduling.create.title': 'Ask learners when they are free',
    'scheduling.create.intro': 'Pick the days and hours to ask about, in the cohort\'s time zone. Every enrolled learner is emailed.',
    'scheduling.create.days': 'Days',
    'scheduling.create.from': 'From',
    'scheduling.create.to': 'Until',
    'scheduling.create.duration': 'Class length (minutes)',
    'scheduling.create.deadline': 'Answer by',
    'scheduling.create.message': 'Note to the learners (optional)',
    'scheduling.create.mailNote': 'A learner without an account answers from a personal link in the email.',
    'scheduling.create.send': 'Send to learners',
    'scheduling.generate.title': 'Generate sessions',
    'scheduling.generate.intro': 'Pick the pattern. One session is created for each live lesson without one, in order, from the cohort\'s first day.',
    'scheduling.generate.prefilled': 'Prefilled from the learners\' best times.',
    'scheduling.generate.closed': 'This cohort is closed, so its sessions cannot be changed.',
    'scheduling.generate.time': 'Starts at',
    'scheduling.generate.duration': 'Length (minutes)',
    'scheduling.generate.zone': 'Times are in the cohort\'s time zone ({zone}).',
    'scheduling.generate.holidays': 'Dates to skip (holidays)',
    'scheduling.generate.addHoliday': 'Skip this date',
    'scheduling.generate.unskip': 'Stop skipping {date}',
    'scheduling.generate.skipDate': 'Skip {date}',
    'scheduling.generate.notify': 'Email the learners their schedule',
    'scheduling.generate.integrated': 'Hold the classes in the Academy classroom',
    'scheduling.generate.preview': 'Preview',
    'scheduling.generate.save': 'Create {count} sessions',
    'scheduling.generate.noDates': 'This pattern gives no date before the cohort ends.',
    'scheduling.generate.colDate': 'Date',
    'scheduling.generate.colTime': 'Time',
    'scheduling.generate.colLesson': 'Lesson',
    'scheduling.generate.flag.instructorAbsent': 'Instructor absent',
    'scheduling.generate.flag.holiday': 'Skipped',
    'scheduling.generate.flag.extraOccurrence': 'No lesson left',
    'scheduling.generate.flag.instructorBusy': 'Another class then',
    'scheduling.generate.extra': '…and {count} more dates with no lesson left.',
    'scheduling.generate.unplaced': 'No date for: {lessons}. Widen the pattern or set a later end date.',
    'scheduling.generate.done': '{count} sessions created, {mailed} learners emailed their schedule.',

    // ── HR's screening test page (screens/screening/ScreeningTestPage.jsx) ──
    'screenTest.title': 'Skills assessment',
    'screenTest.greeting': 'Hello {name},',
    'screenTest.intro': 'Our HR team sent you this assessment to measure the skills the role needs.',
    'screenTest.timeLimit': 'Time limit: {minutes} minutes',
    'screenTest.startBy': 'Start before {date}',
    'screenTest.rules.title': 'Before you start',
    'screenTest.rules.once': 'You can take it once.',
    'screenTest.rules.device': 'Use one device and one browser from start to finish. Once you start, the link stops working anywhere else.',
    'myAssessments.title': 'Skills assessments',
    'myAssessments.subtitle': 'Tests HR has asked you to take. Your results go to HR, not to this page.',
    'myAssessments.linkHint': 'Skills tests HR has sent you',
    'myAssessments.toTake': 'Skills assessments to take',
    'myAssessments.empty': 'No assessments',
    'myAssessments.emptyHint': 'When HR sends you a skills test, it appears here.',
    'myAssessments.startBy': 'Start by {date} · {minutes} minutes',
    'myAssessments.endsAt': 'In progress · ends {date}',
    'myAssessments.submittedOn': 'Submitted {date}',
    'myAssessments.status.ready': 'To take',
    'myAssessments.status.inProgress': 'In progress',
    'myAssessments.status.submitted': 'Submitted',
    'screenTest.rules.timer': 'The timer starts when you press Start and does not stop. When time is up, what you have saved is submitted.',
    'screenTest.rules.autosave': 'Your answers are saved as you go.',
    'screenTest.section.quiz': 'Questions',
    'screenTest.section.task': 'Practical task',
    'screenTest.section.minutes': 'about {minutes} min',
    'screenTest.section.questions': '{count} questions',
    'screenTest.start': 'Start the assessment',
    'screenTest.timeLeft': 'Time left',
    'screenTest.saving': 'Saving…',
    'screenTest.saved': 'All answers saved',
    'screenTest.saveFailed': 'Not saved yet. Check your connection; we will keep trying.',
    'screenTest.points': '{points} pt',
    'screenTest.chooseOne': 'Choose one answer.',
    'screenTest.chooseMany': 'Choose all that apply.',
    'screenTest.answer': 'Your answer',
    'screenTest.chars': '{used} / {max} characters',
    'screenTest.task.answer': 'Your answer',
    'screenTest.task.link': 'Link to your work',
    'screenTest.task.linkHint': 'A full address starting with https://',
    'screenTest.task.file': 'Your file',
    'screenTest.task.upload': 'Upload a file',
    'screenTest.task.replace': 'Replace the file',
    'screenTest.task.uploaded': 'Uploaded: {name}',
    'screenTest.task.uploading': 'Uploading… {percent}%',
    'screenTest.task.uploadFailed': 'The upload failed. Try again.',
    'screenTest.task.uploadsOff': 'File uploads are not available right now. Contact the person who sent you this test.',
    'screenTest.submit': 'Submit the assessment',
    'screenTest.submitConfirm': 'Submit now? You cannot change your answers afterwards.',
    'screenTest.submitYes': 'Yes, submit',
    'screenTest.submitNo': 'Keep working',
    'screenTest.timeUp': 'Time is up. Submitting what you saved…',
    'screenTest.done.title': 'Thank you. Your assessment has been submitted.',
    'screenTest.done.body': 'Our HR team will be in touch about the next steps. You can close this page.',
    'screenTest.done.auto': 'Time ran out, so the answers you had saved were submitted.',
    'screenTest.dead.title': 'This link is not valid',
    'screenTest.dead.body': 'It may have expired, been replaced, already been used, or been opened on another device. Contact the person who sent it to you.',
    'screenTest.error': 'Something went wrong. Check your connection and try again.',
    'screenTest.retry': 'Try again',

    // ── HR's screening marking (screens/grading/Screening*) ──
    'grading.screeningTab': 'Screening (HR)',
    'grading.screening.intro': 'HR’s tests, marked blind: you see the work and a reference, never the name.',
    'grading.screening.empty': 'Nothing to mark',
    'grading.screening.emptyHint': 'Written answers and practical tasks from HR’s tests appear here once submitted.',
    'grading.screening.reference': 'Ref. {reference}',
    'grading.screening.kindQuiz': 'Written answers',
    'grading.screening.kindTask': 'Practical task',
    'grading.screening.aiDrafted': 'AI draft ready',
    'grading.screening.quizTitle': 'Mark a screening quiz',
    'grading.screening.taskTitle': 'Mark a practical task',
    'grading.screening.correction': 'Correction',
    'grading.screening.askAi': 'Ask AI for a draft',
    'grading.screening.aiDraftBadge': 'AI draft',
    'grading.integrity.title': 'Integrity signals',
    'grading.integrity.noFlags': 'No flags',
    'grading.integrity.flag.fastFinish': 'Finished very fast',
    'grading.integrity.flag.leftPage': 'Left the page often',
    'grading.integrity.flag.copiedFromPage': 'Copied from the page',
    'grading.integrity.flag.pastedAnswers': 'Pasted answers',
    'grading.integrity.timeTaken': 'Time taken',
    'grading.integrity.timeWriting': 'Time with the editor open',
    'grading.integrity.durationOfLimit': '{minutes} min {seconds} s of {limit} min',
    'grading.integrity.duration': '{minutes} min {seconds} s',
    'grading.integrity.autoSubmitted': 'submitted when time ran out',
    'grading.integrity.leftPage': 'Left the page',
    'grading.integrity.copied': 'Copied from the page',
    'grading.integrity.pasted': 'Pasted into answers',
    'grading.integrity.timesSeconds': '{count} time(s), {seconds} s in total',
    'grading.integrity.timesChars': '{count} time(s), {chars} characters',
    'grading.integrity.notRecorded': 'The page did not record signals for this work (taken before recording began, or the browser blocked it).',
    'grading.integrity.weighNote': 'For you to weigh, never a mark by itself. The person tested is not told these are recorded.',
    'grading.integrity.answerPasted': '{pasted} of {chars} characters pasted',
    'grading.integrity.aiLeftToYou': 'The AI left this one for you to mark.',
    'grading.integrity.aiFailedBadge': 'AI could not mark',
    'grading.integrity.flagsBadge': '{count} flag(s)',
    'grading.screening.brief': 'The task',
    'grading.screening.handedIn': 'Handed in',
    'grading.screening.aiScore': 'Suggested score: {score} / 100',
    'grading.screening.aiNoScore': 'No score suggested: not enough to go on.',
    'grading.screening.aiNoFile': 'The AI read the written answer and link only, not the file.',
    'grading.screening.scoreLabel': 'Score (0–100)',
};
